import type { ScenarioSpec } from "./types";

/**
 * "The Office Under Attack": one continuous working day in the real
 * office.glb room while an attacker works towards "Project Horizon". Guided
 * camera only (no WASD). Episodes in play order:
 *   1 Reception (tailgate)            2 The suspicious delivery (USB)
 *   3 Office privacy (find, classify) 4 Back at your desk (sign-in)
 *   5 The inbox challenge (11 msgs)   6 The executive call (deepfake CEO)
 *   7 The AI assistant dilemma        8 A quick break (personal laptop)
 *   9 The incident (ransomware, ticket) 10 Closing the loop (bulletin)
 *   11 The follow-up call (live AI vishing)
 * Beat ids keep their historical numbers (b_ep9_*, b_ep10_*, b_ep11_*,
 * b_vish_*); only the titles shown to learners follow the play order.
 * Every decision has a safe branch; safe teach cards with `whatIf` replay
 * the consequence so every learner still sees it.
 */

const ROOM_MIN_X = -12.4;
// Widened from 7.9 -> 8.6 to cover the office-lounge area used by Episode 3's
// walkable "find the risks" hunt (measured live in the glTF at roughly
// x[3.2,8.3] z[1.4,6.4]) — the old wall would have invisibly blocked the
// learner a little short of the real far side of that room.
const ROOM_MAX_X = 8.6;
const ROOM_MIN_Z = -9.7;
const ROOM_MAX_Z = 6.2;
const WALL_H = 3.4;
const PAD = 0.6;

export const officeUnderAttackScenario: ScenarioSpec = {
  version: 1,
  id: "office-under-attack",
  title: "The Office Under Attack",
  summary: {
    goal: "Learn to spot and stop attacks at the door, at your desk, in your inbox, on calls and in AI tools, and to report an incident clearly.",
    youDo: "Deal with a visitor at the door and a USB drive on your desk, sort exposed documents, triage your inbox, take a video call from the CEO, use an AI assistant, file an incident ticket and answer a live phone call.",
    duration: "40-55 min",
    tags: ["physical_security", "usb_devices", "email_phishing", "deepfakes", "ai_use", "voice_phishing", "ransomware", "incident_reporting"],
    audience: ["all_staff"],
    needs: ["sound", "microphone_optional"],
  },
  topic: "Continuous story: recognise danger, decide correctly, report incidents fast",
  language: "en",
  ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3",
  ambientVolume: 0.14,
  music: "none",
  riskMeter: false,
  exposureMap: true,
  passThreshold: 60,
  // Course chapters (Change 10) — one entry per episode-opening camera beat,
  // surfaced as a collapsed "Course chapters" list under the Attack Exposure
  // Map so a learner can jump back and re-watch anything they've already
  // reached (read-only; see store.ts enterReview/exitReview).
  chapters: [
    { id: "ch01", title: "Ep 1 — Reception", beatId: "b_cam_terminal" },
    { id: "ch01b", title: "Ep 1 — Secured Door", beatId: "b_cam_badge" },
    { id: "ch02", title: "Ep 2 — Your Desk", beatId: "b_cam_desk" },
    { id: "ch03", title: "Ep 3 — Office Privacy", beatId: "b_cam_privacy" },
    { id: "ch04", title: "Ep 4 — Signing In", beatId: "b_cam_signin" },
    { id: "ch05", title: "Ep 5 — The Inbox", beatId: "b_cam_inbox" },
    { id: "ch09", title: "Ep 6 — Video Meeting", beatId: "b_cam_meeting" },
    { id: "ch06", title: "Ep 7 — AI Assistant", beatId: "b_cam_ai" },
    { id: "ch10", title: "Ep 8 — Personal Laptop", beatId: "b_cam_personallaptop" },
    { id: "ch11", title: "Ep 9 — Incident Response", beatId: "b_cam_incident" },
    { id: "ch12", title: "Ep 10 — Closing the Loop", beatId: "b_cam_ending_terminal" },
    { id: "ch13", title: "Ep 11 — The Phone Rings", beatId: "b_vish_ring" },
  ],
  // Free movement disabled for now — the glTF room's software-GL walk was
  // unreliable. The course is fully guided (camera on rails). Re-enable
  // `allowFreeExplore` per-scene later, only where walking adds value.
  allowFreeExplore: false,

  scene: {
    environment: {
      gltfUrl: "/cdn/sim/office-vr/office.glb",
      gltfScale: 1,
      gltfPosition: [0, 0, 0],
      gltfRotationDeg: [0, 0, 0],
      // `procedural` is inert for 3D rendering here (gltfUrl always wins in
      // Environment3D) — it exists purely so `.sim-root[data-env="oua_desk"]`
      // can scope this course's own --scr-w/--scr-h + a "click the screen"
      // sticky-note hint (see styles.css), instead of every on-screen device
      // (desklogin/desktop) falling back to the huge generic default sizing
      // that doesn't correspond to any particular monitor in the purchased
      // glTF room.
      procedural: "oua_desk",
      gltfColliders: [
        { min: [ROOM_MIN_X - PAD, 0, ROOM_MIN_Z - PAD], max: [ROOM_MIN_X, WALL_H, ROOM_MAX_Z + PAD] },
        { min: [ROOM_MAX_X, 0, ROOM_MIN_Z - PAD], max: [ROOM_MAX_X + PAD, WALL_H, ROOM_MAX_Z + PAD] },
        { min: [ROOM_MIN_X - PAD, 0, ROOM_MIN_Z - PAD], max: [ROOM_MAX_X + PAD, WALL_H, ROOM_MIN_Z] },
        { min: [ROOM_MIN_X - PAD, 0, ROOM_MAX_Z], max: [ROOM_MAX_X + PAD, WALL_H, ROOM_MAX_Z + PAD] },
      ],
      enableSplat: false,
      enablePano: false,
      backdropColor: "#0d1014",
      exposure: 1.0,
      splat: {},
      deskPc: {
        position: [0.2, 0, 1.7],
        rotationDeg: 180,
        scale: 1,
        screenTitle: "NorthBay Financial",
        userName: "A. Morgan",
        password: "Summer2024!",
        stickyCaption: "portal login",
        // Guided course: teach "click the blinking screen to sign in", not
        // "find a sticky note with the password" — no physical note prop.
        hideStickyNote: true,
      },
      conferenceTv: {
        screenCenter: [-12.3, 1.8, 3.86],
        width: 2.7,
        height: 1.45,
        rotationDeg: 90,
        imageUrl: "/cdn/sim/office-vr/tv-cyberfrog-clouds.png",
      },
      // Cyber-awareness wall posters (Change 5): real framed props baked from
      // an AI illustration + a crisp Canvas2D caption band (OfficePosters.tsx,
      // same "hybrid render" approach as DeskPackage's sticky note / the
      // platform's own poster generator).
      //
      // ROOM (verified 2026-07-26 by loading office.glb directly through
      // three.js's own GLTFLoader in a Node script and reading real
      // `Object3D.matrixWorld`-transformed AABBs -- NOT by clicking through
      // the course, because this room is never visited by any beat/camera
      // waypoint). office-vr/office.glb has exactly 5 distinct room shells,
      // named by their material-group prefix:
      //   01_* = the meeting room,        x[-12.82,-2.82] z[ 1.36, 6.36]
      //   02_* = "your desk" office room, x[  3.18, 8.28] z[ 1.36, 6.36]
      //   04_* = reception + open lounge, x[ -2.82, 3.18] z[-1.84, 6.36]
      //   05_* = an UNFURNISHED 5th room, x[ -2.82, 3.18] z[-10.04,-1.84]
      // Room 05 sits DIRECTLY behind `04_Wall_Reception` (the reception
      // counter's back wall, x[-1.62,1.98] z[-2.19,-1.49], which carries the
      // two wall clocks at z=-1.48 -- that wall is what earlier attempts
      // mounted posters ON, i.e. on its RECEPTION-facing north side, which is
      // why they kept showing up free-floating in the entrance/front-desk
      // room no matter how many times this was "fixed"). Room 05 has its own
      // floor/wall mesh pair (05_Floor / 05_Wall), no hotspot or beat
      // waypoint ever references it, and a real half-height (floor to
      // y=2.8 of a y=3.5 ceiling) partition wall sits dead-centre in it at
      // x~[0.04,0.32] z[-8.04,-5.04] -- this IS the "single half wall in the
      // middle of the room" the room was identified by.
      //
      // IMPORTANT: `04_Wall_Reception` (x[-1.62,1.98]) does NOT span the full
      // x[-2.82,3.18] room width, so there are two ~1.2m-wide, full-height
      // OPEN GAPS beside the counter straight through into room 05 -- this
      // whole area is really one continuous open volume, not sealed rooms.
      // A first attempt that only relocated the posters into room 05 was
      // caught live (via a `?scenario=...` free-look screenshot of the
      // Episode-3 camera) still showing a room-05 poster through one of
      // those gaps from clear across the building. Ad-hoc repositioning
      // can't fix that for good -- any future camera angle could line up
      // with a gap again -- so `wallInfills` below plugs BOTH gaps with real
      // (untextured, dark-neutral) blocking geometry, physically sealing
      // room 05 off. That is the actual fix; the posters merely need to be
      // somewhere sensible inside the now-sealed room.
      //
      // All 10 posters mount on room 05's own walls, facing INTO room 05.
      // `rotationDeg` matches ConferenceTv's convention (0=+Z normal, 90=+X
      // normal, 270=-X normal): back wall (z=-9.9, facing +Z) gets 4, left
      // wall (x=-2.7, facing +X) gets 3, right wall (x=3.05, facing -X) gets
      // 3 -- all clear of the centre partition. Only the Cyberfrog logo
      // poster carries `linkUrl` and is clickable in-world.
      wallInfills: [
        { position: [-2.22, 1.75, -1.84], size: [1.3, 3.5, 0.85], color: "#22262b" },
        { position: [2.58, 1.75, -1.84], size: [1.3, 3.5, 0.85], color: "#22262b" },
      ],
      officePosters: [
        {
          id: "poster_password",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_password.png",
          headline: "PASSWORDS AREN'T FOR SHARING.",
          sub: "Not even with \u201cIT\u201d on the phone.",
          position: [-1.8, 1.75, -9.9],
          rotationDeg: 0,
        },
        {
          id: "poster_phishing",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_phishing.png",
          headline: "LOOK BEFORE YOU CLICK.",
          sub: "Check the sender's real domain.",
          position: [-0.6, 1.75, -9.9],
          rotationDeg: 0,
        },
        {
          id: "poster_usb",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_usb.png",
          headline: "SPOT IT? DON'T PLUG IT.",
          sub: "Unknown USB drives are bait.",
          position: [0.6, 1.75, -9.9],
          rotationDeg: 0,
        },
        {
          id: "poster_badge",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_badge.png",
          headline: "YOUR BADGE, YOUR RESPONSIBILITY.",
          sub: "Never lend it out \u2014 not even for a minute.",
          position: [1.8, 1.75, -9.9],
          rotationDeg: 0,
        },
        {
          id: "poster_tailgate",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_tailgate.png",
          headline: "NO BADGE, NO ENTRY.",
          sub: "Badge in \u2014 every door, every time.",
          position: [-2.7, 1.75, -8.3],
          rotationDeg: 90,
        },
        {
          id: "poster_qr",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_qr.png",
          headline: "SCAN SMART, NOT FAST.",
          sub: "A QR code can hide a bad link too.",
          position: [-2.7, 1.75, -6.4],
          rotationDeg: 90,
        },
        {
          id: "poster_lock",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_lock.png",
          headline: "LOCK IT WHEN YOU WALK.",
          sub: "Win+L takes one second.",
          position: [-2.7, 1.75, -4.5],
          rotationDeg: 90,
        },
        {
          id: "poster_logo",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_security.png",
          headline: "STAY SHARP. STAY SAFE.",
          sub: "Security starts with you",
          position: [3.05, 1.75, -8.3],
          rotationDeg: 270,
        },
        {
          id: "poster_deepfake",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_deepfake.png",
          headline: "SEEING ISN'T BELIEVING.",
          sub: "Verify video calls out-of-band.",
          position: [3.05, 1.75, -6.4],
          rotationDeg: 270,
        },
        {
          id: "poster_shred",
          imageUrl: "/cdn/sim/office-under-attack/posters/poster_shred.png",
          headline: "SHRED IT, DON'T BIN IT.",
          sub: "Paper leaks are still leaks.",
          position: [3.05, 1.75, -4.5],
          rotationDeg: 270,
        },
      ],
      // Verified via a downward raycast grid against the live glTF: the
      // reception counter top ("14_White_Glossy" material) is a flat plane at
      // y=1.265, spanning x[-1.6,2.0] z[-0.7,0.3], directly in front of the
      // reception wall (which carries the two wall clocks at x[-1.06,1.41],
      // z=-1.48). Placed centred on the counter, screen facing +Z (toward the
      // room / an approaching visitor).
      receptionTerminal: {
        position: [0.3, 1.265, -0.15],
        rotationDeg: 0,
        label: "Welcome",
      },
      // Episode 13 — the reception desk phone. Sits on the SAME counter (top
      // plane y=1.265), to the left of the welcome terminal so it doesn't
      // overlap it. Handset faces +Z (toward the room). Pulses while ringing.
      deskPhone: {
        position: [-1.05, 1.265, -0.05],
        rotationDeg: 0,
        scale: 1,
      },
      // Wall-mounted video door station on the dark entrance wall (+Z side, to
      // the side of the glass doors). The camera pushes in on this to start the
      // visitor (tailgate) quest instead of a CCTV card appearing from nowhere.
      // NOTE: wall coordinates are a best-guess pending live calibration — nudge
      // in a real browser with ?intercomPos=x,y,z & ?intercomRot=deg, then bake
      // the good values here. Screen faces -Z (into the room) so rotationDeg=180.
      entryIntercom: {
        position: [3.6, 1.5, 6.0],
        rotationDeg: 180,
        label: "ENTRY",
      },
      // Episode 2's "suspicious delivery" is a real cardboard box sitting on
      // the SAME desk as `deskPc` (not a 2D card) — a labelled shipping
      // sticker + a yellow "Click to open" sticky note propped against the
      // lid. Offset is desk-local [right, up, forward], clear of the
      // monitor/keyboard on the opposite side from the sticky-note hotspot.
      deskPackage: {
        offset: [-0.46, 0.055, 0.02],
        labelText: "Board presentation \u2014 URGENT, load before 2pm",
      },
      // Episode 10 reuses the SAME Desk A laptop already rendered below for
      // Episode 3 \u2014 there is no second/duplicate laptop prop. The sticky
      // note (`stickyCaption` on the "laptop" furniture item) is stamped on
      // its bezel corner; see wp_lounge_laptop below for the matching
      // dead-on camera pose.
      // Episode 3 ("Office privacy"): nine real 3D props planted around the
      // open-plan lounge/desk area east of reception (x[3.2,8.3] z[1.4,6.4]
      // \u2014 see ROOM_MAX_X above), deliberately mixing DIFFERENT physical
      // objects rather than nine repeated paper standees: a laptop screen
      // hosting two apps in sequence (social post, then an internal email),
      // a printer mid-eject, a closed passport, a USB stick plugged into
      // the laptop, one folder, one Post-it, and two sheets of paper lying
      // FLAT on a desk (never standing upright \u2014 see `renderStyle` /
      // `PrivacyRoomProps.tsx`). Furniture below (`furniture`) is the
      // physical desks/shelf/laptop/printer these items visually sit on;
      // item `position`/`rotationDeg` are absolute world coordinates
      // matching the furniture footprints (best-effort, pending a live
      // screenshot pass to nudge). The learner walks there themselves
      // (`b_privacy_hunt.forceFreeRoam`) and clicks each prop in situ; the
      // SAME nine items are then dragged into the four sensitivity levels
      // in Phase B (`privacy_classify` device, no 3D).
      privacyRoom: {
        // Desk B is NOT a procedural desk \u2014 the base office.glb already has
        // a real wood desk right there (a duplicate rendered box behind it
        // just doubled up on the same spot, overlapping the room's own
        // chair). Verified with a downward-raycast grid (same technique as
        // the reception-counter calibration above): a real "22_Wood_legs"
        // desk surface sits flat at x[5.8,6.4] z[4.0,5.4] y=0.75, clear of
        // any other base-room geometry \u2014 the printer/passport/brochure
        // below are positioned directly on THAT real surface instead.
        furniture: [
          // Desk A (laptop + sticky note + a loose paper) \u2014 this one has
          // no equivalent real desk nearby, so it stays procedural.
          { kind: "desk", position: [4.3, 0, 2.3], rotationDeg: 0 },
          // Wall shelf near the far (east) wall, holding the acquisition folder.
          { kind: "shelf", position: [8.3, 0, 2.4], rotationDeg: 90 },
          // Two more boards on the SAME wall stack (Change a, 2026-07-27):
          // Desk A was carrying 5 of the 9 hunt items in one tight cluster —
          // moved `password_note` + `meeting_agenda` here (below/above the
          // existing folder board) to spread the hunt across the room instead
          // of one crowded corner. `Shelf`'s board always renders at a FIXED
          // internal height (`SHELF_H`) above whatever y this furniture
          // position carries, so varying y here stacks real boards.
          { kind: "shelf", position: [8.3, -0.45, 2.4], rotationDeg: 90 },
          { kind: "shelf", position: [8.3, 0.55, 2.4], rotationDeg: 90 },
          // Laptop on Desk A \u2014 screen faces roughly toward the room entrance.
          // Episode 10 reuses THIS SAME laptop as "your own laptop" (no
          // second laptop prop): the sticky note stamped here is only
          // rendered/clickable once Episode 10's beats are reached (App.tsx
          // gates the hotspot on device kind + screenOn), so it does not
          // interfere with Episode 3's earlier "find the risks" hunt.
          {
            kind: "laptop",
            position: [4.45, 0.74, 2.15],
            rotationDeg: 270,
            stickyCaption: "This is your own laptop \u2014 click to open it",
          },
          // Small desktop printer \u2014 sits on the REAL desk surface (see note above).
          { kind: "printer", position: [6.15, 0.75, 4.2], rotationDeg: 0 },
        ],
        items: [
          {
            id: "social_post",
            label: "Approved social post",
            icon: "\uD83D\uDCF1",
            level: "public",
            card: {
              title: "Approved social post",
              informationType: "Public information",
              verdict: "display",
              verdictLabel: "Can stay on display",
              found: "The laptop is open on the company's social page. The post is about last week's industry summit and has already been public for days.",
              matters: "It was written and approved for anyone to see. It does not include customer details, pay or internal plans.",
              action: "This page can stay on the screen. Close the inbox underneath it if a visitor is nearby, so the staff email is not visible as well.",
            },
            renderStyle: "screen",
            reveal:
              "The laptop is open on the company\u2019s own social page \u2014 a post about last week\u2019s industry " +
              "summit, already public for days and getting a normal amount of engagement.",
            prompt: "Not everything on a screen is a risk. What makes this post different from what\u2019s below it?",
            explain:
              "This was written and approved to be shared with the whole world \u2014 there\u2019s nothing here that " +
              "needs protecting, so it sits at the lowest sensitivity tier.",
            position: [4.56, 0.9, 2.15],
          },
          {
            id: "internal_email",
            label: "Internal event email",
            icon: "\u2709\uFE0F",
            level: "internal",
            card: {
              title: "Internal event email",
              informationType: "Staff information",
              verdict: "screen",
              verdictLabel: "Protect the screen",
              found: "Under the social post, the inbox is open on a staff-wide email about the summer rooftop party. It shows the date, the RSVP deadline and the catering headcount.",
              matters: "The email is for employees. A visitor reading over a shoulder learns internal plans, even though it contains no customer records.",
              action: "Close the inbox, or turn the screen away, before someone who does not work here can read it. Do not forward it outside the organisation.",
            },
            renderStyle: "screen",
            reveal:
              "Right below the social post, an open inbox shows a staff-wide email about the summer rooftop " +
              "party \u2014 date, RSVP deadline, catering headcount.",
            prompt: "Would it hurt anyone if a visitor read this over your shoulder? Who is it actually meant for?",
            explain:
              "Staff logistics like this are fine for employees and authorised contractors to see, but they " +
              "weren\u2019t written for the public \u2014 one level above the post just above it on the same screen.",
            position: [4.56, 0.8, 2.15],
          },
          {
            id: "usb_customer_records",
            label: "USB stick with customer records",
            icon: "\uD83D\uDD0C",
            level: "restricted",
            card: {
              title: "USB stick with customer records",
              informationType: "Customer and financial information",
              verdict: "secure",
              verdictLabel: "Put away securely",
              found: "A USB stick is plugged into the side of the laptop. It contains a client list with names, account numbers and this year's revenue by customer.",
              matters: "The whole customer list fits in a pocket. Account numbers and revenue can be copied or misused if the stick is taken.",
              action: "Unplug it and return it to the person responsible, or lock it in approved storage. Do not leave it in the laptop or copy the files onto another computer.",
            },
            tags: ["personal", "financial"],
            renderStyle: "usb",
            reveal:
              "A USB stick is plugged into the side of the laptop. Opening it reveals a client list \u2014 " +
              "names, account numbers and this year\u2019s revenue by customer.",
            prompt: "If this stick walked out the door in someone\u2019s pocket, who would you have to notify?",
            explain:
              "Client names and account numbers mixed with real financial data are Highly Confidential / " +
              "Restricted \u2014 and unlike a printed page, a whole customer database fits in a pocket.",
            position: [4.3, 0.755, 2.32],
            rotationDeg: 270,
          },
          {
            id: "salary_printout",
            label: "Salary comparison printout",
            icon: "\uD83D\uDDA8\uFE0F",
            level: "confidential",
            card: {
              title: "Salary comparison printout",
              informationType: "Pay information",
              verdict: "secure",
              verdictLabel: "Put away securely",
              found: "A page titled \"Internal Salary Comparison\" is still half out of the printer. It is a department-by-department pay chart.",
              matters: "Pay information is personal. Anyone walking past the printer can pick the page up and read it.",
              action: "Collect the page and give it to the person who printed it, or put it in approved storage. Do not leave it on the tray or in an open recycling bin.",
            },
            tags: ["financial", "personal"],
            renderStyle: "printer_output",
            reveal:
              "A page titled \u201CInternal Salary Comparison\u201D is still half out of the printer \u2014 a " +
              "department-by-department pay chart, left for anyone walking past to pick up.",
            prompt: "It\u2019s about people, but it\u2019s also core business data. Who genuinely needs to see it fresh off the tray?",
            explain:
              "Payroll mixes personal data with sensitive financial data \u2014 confidential either way, and a " +
              "leak damages trust and competitiveness at the same time. Printed pages are easy to forget on a tray.",
            position: [6.15, 0.91, 4.3],
          },
          {
            id: "passport_id",
            label: "Passport or ID card",
            icon: "\uD83D\uDEC2",
            level: "restricted",
            card: {
              title: "Passport or ID card",
              informationType: "Personal identity information",
              verdict: "secure",
              verdictLabel: "Put away securely",
              found: "An identity document left open on a desk, showing a name, photo, document number and date of birth.",
              matters: "Someone could photograph these details and misuse them for identity fraud or impersonation.",
              action: "Return it to the authorised person or approved secure storage. Do not photograph or copy it.",
            },
            tags: ["personal"],
            renderStyle: "passport",
            reveal:
              "A passport sits open on the desk, clipped to an HR file \u2014 full name, photo, passport number " +
              "and date of birth, all visible.",
            prompt: "If this walked out the door, what could someone do with it?",
            explain:
              "Government ID documents are Highly Confidential / Restricted \u2014 they combine identity data " +
              "with a number that\u2019s often used to verify who you are elsewhere. Very few people should ever " +
              "need to see the original.",
            position: [6.1, 0.755, 4.75],
          },
          {
            id: "password_note",
            label: "Sticky note with a password",
            icon: "\uD83D\uDD11",
            level: "restricted",
            card: {
              title: "Sticky note with a password",
              informationType: "Login credentials",
              verdict: "secure",
              verdictLabel: "Put away securely",
              found: "A sticky note at the edge of the desk reads \"Wifi + VPN: Summer2024!\". It is a working password for the office network.",
              matters: "Anyone who reads the note can use the password immediately. Leaving it in the open is the same as sharing access.",
              action: "Take the note to the account owner so the password can be changed. Do not photograph it or type it into another device.",
            },
            tags: ["credentials"],
            renderStyle: "flat",
            size: "small",
            reveal:
              "A Post-it stuck to the shelf: \u201CWifi + VPN: Summer2024!\u201D \u2014 a real working " +
              "password, written down for anyone walking past.",
            prompt: "If you found this in ten seconds, how long would it take someone who actually wanted in?",
            explain:
              "Credentials are Highly Confidential / Restricted no matter how ordinary they look \u2014 anyone " +
              "who finds a working password can use it immediately, so it needs the strictest handling of " +
              "anything on this list.",
            // Moved off Desk A onto the lower shelf board (Change a,
            // 2026-07-27) to break up the 5-item desk cluster.
            position: [8.3, 0.61, 2.3],
            rotationDeg: 90,
          },
          {
            id: "project_falcon_folder",
            label: "Project Falcon folder",
            icon: "\uD83D\uDCC1",
            level: "restricted",
            card: {
              title: "Project Falcon folder",
              informationType: "Unannounced deal information",
              verdict: "secure",
              verdictLabel: "Put away securely",
              found: "A folder of draft documents for an unannounced acquisition, code-named Project Falcon, is sitting on an open shelf.",
              matters: "The deal has not been announced. Someone who reads or copies the folder can expose both organisations and damage the deal.",
              action: "Return the folder to the deal owner or lock it in approved storage. Do not photograph, copy or discuss it outside the authorised team.",
            },
            tags: ["legal", "ip"],
            renderStyle: "flat",
            folder: true,
            reveal:
              "A folder of draft acquisition documents for an unannounced deal, code-named \u201CProject " +
              "Falcon,\u201D left out on the open shelf.",
            prompt: "If this leaked before the announcement, who would be affected \u2014 and how badly?",
            explain:
              "Unannounced merger and acquisition material is Highly Confidential / Restricted \u2014 an early " +
              "leak can move markets, break deals, and expose the company (and the other side) to real legal " +
              "and financial harm.",
            position: [8.3, 1.06, 2.4],
            rotationDeg: 90,
          },
          {
            id: "company_brochure",
            label: "Company brochure",
            icon: "\uD83D\uDCF0",
            level: "public",
            card: {
              title: "Company brochure",
              informationType: "Public information",
              verdict: "display",
              verdictLabel: "Can stay on display",
              found: "An approved marketing brochure, also published on the company website.",
              matters: "This version is intended for public sharing. It contains no private notes or customer details.",
              action: "It can remain on display. Check that no confidential notes or papers are attached.",
            },
            renderStyle: "flat",
            reveal:
              "A glossy marketing brochure \u2014 the exact same one available for download on the public website.",
            prompt: "Not everything sitting on a desk is a risk. What makes this one different?",
            explain:
              "This was designed to be shared publicly \u2014 nothing here needs protecting, and treating " +
              "everything as secret just slows people down.",
            position: [6.15, 0.746, 5.15],
            rotationDeg: -15,
          },
          {
            id: "meeting_agenda",
            label: "Team meeting agenda",
            icon: "\uD83D\uDDD2\uFE0F",
            level: "internal",
            card: {
              title: "Team meeting agenda",
              informationType: "Internal staff information",
              verdict: "check",
              verdictLabel: "Check before sharing",
              found: "Next week's team meeting agenda is lying on the table. It was written for the team, not for people outside the organisation.",
              matters: "A visitor can read topics that were only meant for staff. It is not customer data, but it is still not a public document.",
              action: "Take it with you when you leave, or turn it face down if a visitor is in the room. Do not leave it on a chair a guest will use.",
            },
            renderStyle: "flat",
            reveal: "Next week's team meeting agenda \u2014 useful for staff, but not written for outside eyes.",
            prompt:
              "Would it really hurt anyone if a visitor read this over your shoulder? Who is it actually meant for?",
            explain:
              "Internal material like this is fine for employees and authorised contractors to see, but it " +
              "isn't meant for the public \u2014 a level below confidential, but still not something to leave " +
              "on a visitor's chair.",
            // Moved off Desk A onto the upper shelf board (Change a,
            // 2026-07-27) to break up the 5-item desk cluster.
            position: [8.3, 1.61, 2.5],
            rotationDeg: 90,
          },
        ],
      },
    },
    props: [],
    waypoints: [
      {
        // Start in the entrance room (visitor side, +Z), looking across the
        // desk toward the reception counter and the clocks wall.
        id: "wp_reception_wide",
        position: [1.2, 1.7, 4.6],
        target: [0.3, 1.35, -0.3],
        fov: 66,
        durationS: 0,
      },
      // Scene-1 zoom: walk up from the entrance and frame the welcome-terminal
      // kiosk screen DEAD-ON and perpendicular (screen centre = [0.3, 1.75,
      // -0.174], vertical, facing +Z). Camera sits exactly in front at the same
      // x/y so the DOM terminal overlay lines up on the 3D screen face. At d=0.39
      // and fov 40 the 0.186-tall screen fills ~66% of viewport height, which the
      // --scr-* vars in styles.css mirror (--scr-h: 66vh). Zoomed in tight — the
      // screen dominates the frame; base/neck fall below it.
      {
        id: "wp_terminal_close",
        position: [0.3, 1.75, 0.213],
        target: [0.3, 1.75, -0.174],
        fov: 40,
        durationS: 3.0,
        ease: "easeInOut",
      },
      // Episode 13: push in on the ringing reception desk phone (at
      // [-1.05, 1.265, -0.05]) from the visitor side, framing it just off-centre.
      {
        id: "wp_reception_phone",
        position: [-0.7, 1.62, 1.5],
        target: [-1.05, 1.3, -0.05],
        fov: 46,
        durationS: 2.6,
        ease: "easeInOut",
      },
      // Legacy Episode-1 glide waypoints (enter -> look around -> establish).
      // No longer wired into the opening (Scene 1 now goes entrance -> terminal
      // zoom directly) but kept for future episodes / QA deep-links.
      {
        id: "wp_room_enter",
        position: [0.6, 1.8, -1.6],
        target: [-1.8, 1.2, -0.8],
        fov: 62,
        durationS: 2.6,
        ease: "easeInOut",
      },
      {
        id: "wp_room_lookaround",
        position: [-0.5, 1.85, -2.2],
        target: [-7.0, 1.4, 1.0],
        fov: 68,
        durationS: 3.0,
        ease: "easeInOut",
      },
      {
        id: "wp_reception_establish",
        position: [1, 1.65, -0.5],
        target: [2.4, 1.15, 5],
        fov: 62,
        durationS: 2.6,
        ease: "easeInOut",
      },
      {
        id: "wp_reception_badge",
        position: [1.5, 1.48, 1.4],
        target: [2.4, 1.15, 5],
        fov: 50,
        durationS: 2.4,
        ease: "easeInOut",
      },
      // Scene-1b: turn from the counter to the entrance wall and push in on the
      // wall-mounted video door station (entryIntercom at [3.6,1.5,6.0], screen
      // facing -Z). Camera sits in front of it (smaller z, same x/y) looking +Z
      // so the intercom screen fills the frame before the visitor speaks. Tune
      // alongside the intercom position (?intercomPos=).
      {
        id: "wp_intercom_close",
        position: [3.6, 1.55, 4.7],
        target: [3.6, 1.5, 6.0],
        fov: 42,
        durationS: 2.6,
        ease: "easeInOut",
      },
      {
        id: "wp_desk_glide",
        position: [0.5, 1.5, 0.6],
        target: [0.2, 1.05, 1.7],
        fov: 52,
        durationS: 2.8,
        ease: "easeInOut",
      },
      // Meeting-room waypoints — authored ahead of Episodes 8/9/10 (news report /
      // deepfake call / secure-the-meeting), which all use the wall-TV
      // (`environment.conferenceTv`) on the far (west, x≈-12.3) wall. Not wired
      // into any beat/edge yet — future episodes attach beats to these ids.
      {
        id: "wp_meeting_establish",
        position: [-3.5, 2.15, 3.86],
        target: [-12.3, 1.7, 3.86],
        fov: 62,
        durationS: 2.4,
        ease: "easeInOut",
      },
      {
        id: "wp_meeting_tv_close",
        position: [-7.4, 1.62, 3.86],
        target: [-12.3, 1.8, 3.86],
        fov: 44,
        durationS: 2.2,
        ease: "easeInOut",
      },
      // Episode 3 — the threshold of the office lounge (x[3.2,8.3] z[1.4,6.4]).
      // The camera glides here on rails, then `b_privacy_hunt.forceFreeRoam`
      // hands control to FirstPersonControls for the walk-and-click hunt; this
      // waypoint's position/target double as the FP spawn point + facing.
      {
        id: "wp_privacy_entrance",
        position: [2.6, 1.68, 2.0],
        target: [5.4, 1.2, 3.6],
        fov: 62,
        durationS: 2.6,
        ease: "easeInOut",
      },
      // Look-only counterpart to wp_privacy_entrance (Change a, 2026-07-25,
      // repositioned 2026-07-27): that waypoint sits at x=2.6, OUTSIDE the
      // lounge's own bounds (x>=3.2) by design — it's the FP walk spawn
      // point, meant to be walked away from. A stationary look-around camera
      // parked there instead just spun in place at the threshold, so half
      // the turn showed the reception area / exterior behind it, not the
      // room being searched.
      //
      // 2026-07-27: even backed off the threshold, a generic 360 spin here
      // still turned to face doorways/windows a couple of metres away,
      // reading as "walked outside" — see `spinInPlace`/`spinYawLimitDeg` in
      // App.tsx/Scene.tsx, which now cap the look-around to a forward cone
      // instead of a full circle.
      //
      // Standing point tuned live (2026-07-27, 2nd pass): the very first
      // attempt backed right into the room's NW corner (x=3.8,z=1.7), only
      // ~0.6m from both the west AND north walls — at the yaw limit the
      // learner ended up staring straight into one of those walls from
      // point-blank range (an ugly close-up blur, not exterior, but still
      // wrong). Pulled forward to x=4.2,z=3.3 (~1.0m/1.9m from the nearest
      // walls) so every yaw extreme still shows several metres of open room.
      // `target` points at the rough centroid of the three item clusters —
      // Desk A (~4.3-4.7, z 2.1-2.5), the shelf stack (~8.3, z 2.2-2.6), and
      // the real desk/printer (~6.1-6.4, z 4.0-5.4) — so `spinYawLimitDeg`
      // only has to swing ~70 either way to reach the widest of the three
      // (Desk A, the most off-axis one) instead of a full circle.
      {
        id: "wp_privacy_lookonly",
        position: [4.2, 1.68, 3.3],
        target: [6.33, 1.0, 3.07],
        fov: 62,
        durationS: 0,
      },
      // Episode 10 — push in from the lounge threshold to a DEAD-ON,
      // PERPENDICULAR pose facing Desk A's laptop screen (the SAME laptop
      // furniture prop used by Episode 3, `three/PrivacyRoomProps.tsx`'s
      // `Laptop`) \u2014 there is no separate/second laptop model. Same
      // convention as wp_desk's math comment above, but the screen has a
      // real hinge tilt (tiltBackDeg=12) this time, so the tilt is folded
      // into the normal too.
      //
      // Math (furniture laptop: position [4.45,0.74,2.15], rotationDeg 270;
      // baseH=0.014, baseD=0.21, screenW=0.28, screenH=0.18, screenT=0.01,
      // tiltBackDeg=12). Active-LCD local point inside the screen sub-group
      // (before tilt): [0, screenH/2(0.09), screenT/2+0.0006(0.0056)].
      // Rotating by -tiltBackDeg (12deg) about X: y'=0.08920, z'=-0.01323.
      // Add the sub-group's own offset [0, baseH(0.014), -baseD/2(-0.105)]:
      // local (laptop-group) point = [0, 0.10320, -0.11823]. Rotating that
      // by rotationDeg=270 about Y (cos=0, sin=-1): x'=0.11823, z'=0,
      // y unchanged \u2192 world centre = (4.45+0.11823, 0.74+0.10320, 2.15)
      //                                 = (4.568, 0.843, 2.15).
      // Screen normal (0,0,1) through the same tilt+yaw \u2192
      // (-0.97815, 0.20791, 0). Distance solved so the active LCD's
      // (screenH*0.86=0.1548m) height fills 62% of viewport height at fov
      // 36 (same target fraction as office/hotel_suite/oua_desk's --scr-h,
      // applied via .plt-wrap's own 62vh sizing): D = 0.1548/(2*0.62*
      // tan(18deg)) = 0.1548/0.4029 \u2248 0.3842m. Camera sits along the
      // screen's OUTWARD normal at that distance, looking straight back at
      // the centre (zero lateral/vertical offset):
      //   camera position = centre + normal*D
      //                    = (4.568-0.97815*0.3842, 0.843+0.20791*0.3842, 2.15)
      //                    = (4.192, 0.923, 2.15)
      //   camera target    = centre = (4.568, 0.843, 2.15)
      {
        id: "wp_lounge_laptop",
        position: [4.192, 0.923, 2.15],
        target: [4.568, 0.843, 2.15],
        fov: 36,
        durationS: 2.0,
        ease: "easeInOut",
      },
      // Episode 4 — back at the desk, DEAD-ON + PERPENDICULAR to the monitor
      // screen for pixel-perfect DOM-overlay alignment (sign-in / MFA-fatigue
      // / desktop / inbox / AI-assistant / incident device beats all reuse
      // this exact pose — see task (c): the camera must NOT move again once
      // the learner is looking at the screen).
      //
      // Math (deskPc: position [0.2,0,1.7], rotationDeg 180, scale 1; the
      // DeskWorkstation.tsx screen mesh is a 0.58 x 0.34 plane at local
      // offset [0, DESK_H(0.74)+0.46, -0.07+0.018] = [0,1.2,-0.052] inside
      // that group). Rotating a local point by 180 deg about Y negates both
      // x and z, so:
      //   screen world centre = deskPc.position + (-0, 1.2, -(-0.052))
      //                        = (0.2, 1.2, 1.752)
      //   screen world normal  = Ry(180) * (0,0,1) = (0,0,-1)
      //     -> camera must sit at smaller Z, looking toward +Z.
      // Camera position/target share the EXACT same x (0.2) and y (1.2) as
      // the screen centre (zero lateral/vertical offset = truly dead-on, no
      // off-axis skew a rectangular DOM overlay could never match). Distance
      // solved so the 0.34m-tall screen fills EXACTLY 62% of viewport height
      // at fov 36 (same vertical-FOV-locks-a-height-fraction principle as
      // the "office"/"hotel_suite" procedural rooms, and the same target
      // fraction as this course's --scr-h in styles.css):
      //   halfFovRad = 18deg = 0.3141593 rad; tan(18deg) = 0.3249197
      //   d = 0.34 / (2 * 0.62 * tan(18deg)) = 0.34 / 0.4029004 = 0.8436895
      //   cameraZ = screenZ - d = 1.752 - 0.8436895 = 0.9083105
      {
        id: "wp_desk_signin",
        position: [0.2, 1.2, 0.9083105],
        target: [0.2, 1.2, 1.752],
        fov: 36,
        durationS: 2.6,
        ease: "easeInOut",
      },
      // Episode 5 onward (inbox / AI assistant / incident) — IDENTICAL pose
      // to wp_desk_signin on purpose: the learner stays looking at the same
      // spot on the same monitor for every desk-screen task, the camera
      // waypoint transition is a no-op (0 actual movement), and the DOM
      // overlay never needs to re-align mid-course.
      {
        id: "wp_desk_inbox",
        position: [0.2, 1.2, 0.9083105],
        target: [0.2, 1.2, 1.752],
        fov: 36,
        durationS: 2.4,
        ease: "easeInOut",
      },
    ],
    initialWaypointId: "wp_reception_wide",
  },

  devices: [
    {
      id: "terminal_welcome",
      kind: "terminal",
      config: {
        title: "NORTHBAY FINANCIAL \u2014 FRONT DESK",
        offLabel: "Switch on the welcome terminal",
        lines: [
          "Welcome to NorthBay Financial. Project Horizon goes to the board today.",
          "The front desk is unattended \u2014 use this terminal or the intercom for any access request.",
          "Only badge-verified staff and pre-registered visitors may enter the secured floor.",
        ],
        continueLabel: "Head to the entrance",
      },
    },
    {
      // A persistent impersonator at the secured side door who ESCALATES across
      // three rounds (friendly courier -> name-drops an exec -> open pressure &
      // a veiled threat) — each clip is a real lip-synced talking-head. Complying
      // at ANY round opens the door (risky); holding firm through all three makes
      // them storm off (safe). Round messages are VERBATIM copies of the HeyGen
      // clips rendered by ops/render-oua-visitor.mjs — keep them in lockstep.
      id: "tailgate_decision",
      kind: "tailgate",
      config: {
        station: "Side Entrance \u2014 Camera 2",
        visitorName: "Delivery Visitor",
        visitorOrg: "No visible badge, not on today's list",
        // legacy single-message fallback (unused once rounds[] is set):
        message:
          "Hey, perfect timing! I've got the board packets for the two o'clock. My hands are completely full and my badge pass is in the van. Could you just grab the door for me?",
        subtext: "They're right at the secured door, arms full of boxes, smiling warmly.",
        giveupClipUrl: "/cdn/sim/office-under-attack/clips/visitor_giveup.mp4",
        giveupMessage: "Unbelievable. Fine \u2014 I'll go sign in at reception like everybody else.",
        rounds: [
          {
            id: "round1_friendly",
            clipUrl: "/cdn/sim/office-under-attack/clips/visitor_round1.mp4",
            tag: "Friendly \u00b7 hands full",
            message:
              "Hey, perfect timing! Meridian Print \u2014 I've got the board packets for the two o'clock. My hands are completely full and my badge pass is in the van. Could you just grab the door for me?",
            complyLabel: "Hold the door \u2014 their hands are full",
            refuseLabel: "Ask them to sign in at reception first",
            refuseCategory: "verify",
          },
          {
            id: "round2_namedrop",
            clipUrl: "/cdn/sim/office-under-attack/clips/visitor_round2.mp4",
            tag: "Now name-dropping an executive",
            message:
              "These are for Project Horizon \u2014 the board meeting. I was on the phone with Marcus Keller's office twenty minutes ago; his assistant said someone would let me straight up. You can call her if you want, but she's in the pre-brief right now.",
            complyLabel: "It's for the board \u2014 let them in",
            refuseLabel: "Reception can verify that in a minute \u2014 hold firm",
            refuseCategory: "verify",
          },
          {
            id: "round3_pressure",
            clipUrl: "/cdn/sim/office-under-attack/clips/visitor_round3.mp4",
            tag: "Pressure \u00b7 veiled threat",
            message:
              "Okay, listen. I just spoke to your boss, and he is not happy. If the board sits down at two and these packets aren't in that room, I will make sure everyone knows exactly who kept me standing at this door. What's your name?",
            complyLabel: "Give in \u2014 open the door before this escalates",
            refuseLabel: "Stay calm and polite, but do not open the door",
            refuseCategory: "refuse",
          },
        ],
      },
    },
    {
      id: "tailgate_retry",
      kind: "tailgate",
      config: {
        station: "Side Entrance \u2014 Camera 2",
        visitorName: "Delivery Visitor",
        visitorOrg: "No visible badge, not on today's list",
        clipUrl: "/cdn/sim/office-under-attack/clips/visitor_retry.mp4",
        message: "Come on, it's just a couple of boxes \u2014 you saw me a minute ago.",
        subtext: "Same visitor, same door. This time you know better.",
        forceSafe: true,
        retryHint: "This time, do the right thing: every visitor checks in at reception first.",
        refuseLabel: "Direct them to reception",
      },
    },
    {
      id: "usb_decision",
      kind: "usbdevice",
      config: {
        mode: "decision",
        usbLabel: "Project Horizon \u2014 Updated Board Slides",
        noteText: "USB stick inside \u2014 please plug it in and load it before the 2pm board meeting.",
        senderLine: "\u2014 left at your desk, no name",
        options: [
          {
            id: "insert_reception",
            label: "Plug it into the reception PC to see what's on it",
            key: "risky",
            eventId: "inserted_usb_workstation",
          },
          {
            id: "insert_personal",
            label: "Plug it into your own laptop instead",
            key: "risky",
            eventId: "inserted_usb_personal",
          },
          {
            id: "report",
            label: "Contact the sender through a known channel, then hand it to IT/Security unopened",
            key: "safe",
            eventId: "reported_suspicious_usb",
          },
        ],
      },
    },
    {
      id: "privacy_hunt",
      kind: "privacyhunt",
      config: {
        headline: "Episode 3 \u2014 Find the risks",
        body:
          "Take a couple of minutes to look around the lounge area. Click on anything you can find \u2014 " +
          "most of it shouldn't be left out in the open, but a couple of things are actually fine. Each one " +
          "you inspect tells you which.",
        timerSeconds: 120,
      },
    },
    {
      id: "privacy_classify",
      kind: "privacyclassify",
      config: {
        headline: "Sort what you found",
        body:
          "Drag each card into the level it really belongs to. There's no penalty for guessing \u2014 every " +
          "card explains itself once you drop it. You can finish once you've sorted about half of them.",
        categories: [
          {
            id: "public",
            label: "Public",
            hint: "Approved for anyone to see \u2014 already published, or meant to be shared freely.",
          },
          {
            id: "internal",
            label: "Internal",
            hint: "For employees and authorised contractors \u2014 not written for the public.",
          },
          {
            id: "confidential",
            label: "Confidential",
            hint: "Disclosure could harm employees, customers, or the company.",
          },
          {
            id: "restricted",
            label: "Highly Confidential / Restricted",
            hint: "The most sensitive tier \u2014 access limited to specifically authorised people.",
          },
        ],
      },
    },
    {
      id: "usb_recovery",
      kind: "usbdevice",
      config: {
        mode: "recovery",
        situationLine:
          "The screen flickered. A terminal window flashed open, ran something, and closed itself. " +
          "Everything looks normal again \u2014 or does it?",
        options: [
          {
            id: "hide_it",
            label: "Say nothing and slide the USB into a drawer",
            key: "risky",
            eventId: "hid_infected_usb",
          },
          {
            id: "remove_continue",
            label: "Pull the USB out and get back to work",
            key: "not_reported",
            eventId: "removed_usb_no_report",
          },
          {
            id: "restart_pc",
            label: "Restart the PC \u2014 that should clear it",
            key: "not_reported",
            eventId: "restarted_without_reporting",
          },
          {
            id: "report_immediately",
            label: "Report it to IT/Security right now",
            key: "reported",
            eventId: "reported_after_mistake",
          },
        ],
      },
    },
    {
      id: "desklogin_decision",
      kind: "desklogin",
      config: {
        screenTitle: "NorthBay Financial \u2014 locked",
        userName: "A. Morgan",
        hint: "The credentials are shown in the note on-screen \u2014 click it to copy them in, or type your own.",
        credential: {
          email: "a.morgan@northbayfinancial.com",
          password: "Summer2024!",
          source: "Saved note",
        },
        continueLabel: "Sign In",
      },
    },
    {
      id: "desktop_home",
      kind: "desktop",
      config: {
        clock: "09:07",
        date: "Mon 16 May",
        icons: [
          { id: "pdf", label: "Security_Briefing.pdf", icon: "pdf", opens: "pdf" },
          { id: "outlook", label: "Outlook", icon: "outlook", opens: "outlook" },
          { id: "teams", label: "Teams", icon: "teams", opens: "teams" },
          { id: "chrome", label: "Chrome", icon: "chrome", opens: "chrome" },
          { id: "files", label: "File Explorer", icon: "files", opens: "files" },
        ],
        taskbar: [
          { id: "outlook", label: "Outlook", icon: "outlook", opens: "outlook" },
          { id: "teams", label: "Teams", icon: "teams", opens: "teams" },
          { id: "chrome", label: "Chrome", icon: "chrome", opens: "chrome" },
          { id: "files", label: "File Explorer", icon: "files", opens: "files" },
        ],
        // Deliberately SHORT — one screen, zero scrolling, on a 62vh-tall
        // monitor overlay. One section, three tight bullets, four red flags.
        // Prior 3-section version (kept in git history) badly overflowed the
        // visible screen box and forced scrolling to reach the close button.
        pdfBriefing: {
          fileName: "Security_Briefing.pdf",
          title: "Spotting a Phishing Attack",
          subtitle: "60 seconds before you open your inbox.",
          legitDomain: "northbayfinancial.com",
          legitLoginUrl: "https://portal.northbayfinancial.com/login",
          sections: [
            {
              heading: "Three checks before you act",
              body:
                "Real NorthBay pages only ever live under northbayfinancial.com. A padlock icon proves " +
                "nothing \u2014 attackers get padlocks too.",
              bullets: [
                "Domain looks almost right? Treat it as fake (northbay-id-secure.com \u2260 northbayfinancial.com).",
                "Hover before you click \u2014 the visible text can point anywhere.",
                "Never enable macros or run a downloaded file just to \u201Cview\u201D it.",
              ],
            },
          ],
          redFlags: [
            "Sender domain that's almost right, but not exact.",
            "Urgency measured in hours, pushing you to skip checking.",
            "A link or QR code that leads to a login page, not the promised content.",
            "A request to enable macros or run a downloaded file.",
          ],
          continueLabel: "Got it \u2014 open my inbox",
        },
      },
    },
    {
      id: "inbox_triage",
      kind: "inboxtriage",
      config: {
        appTitle: "Outlook \u2014 Inbox",
        submitLabel: "Submit my triage",
        incompleteHint: "Decide what to do with at least a few more messages before you submit \u2014 the more you review, the better.",
        // Learner must triage at least 5 of the 11 messages (not force every
        // single one) before Submit unlocks; reviewing more is still always
        // encouraged and scored higher.
        minVerdicts: 5,
        scoredMessageIds: [
          "m_phish_link",
          "m_malware_zip",
          "m_bec_invoice",
          "m_qr_benefits",
          "m_horizon_access",
          "m_legit_reset",
        ],
        emails: [
          {
            id: "m_phish_link",
            from: "IT Security",
            fromAddress: "it-security@northbay-id-secure.com",
            to: "you",
            receivedAt: "08:12",
            subject: "Action required: your password expires in 2 hours",
            preview: "Your NorthBay Financial password is about to expire. Verify now to avoid being locked out\u2026",
            unread: true,
            correctVerdict: "report",
            body:
              "Your NorthBay Financial network password expires in 2 hours.\n\n" +
              "To avoid losing access to your account, verify your credentials now using the secure link below.",
            cta: {
              label: "Verify Password",
              url: "http://northbay-id-secure.com/verify?u=a.morgan",
              displayUrl: "northbay-id-secure.com/verify",
              sim: {
                displayUrl: "northbay-id-secure.com/verify",
                sslWarning: true,
                page: {
                  kind: "login",
                  legit: false,
                  brand: "NorthBay Financial",
                  logoMismatch: true,
                  headline: "Verify your password to continue",
                  body: "Your session will expire in 2 hours. Enter your credentials below to keep your account active.",
                },
              },
            },
            verdictFeedback: {
              report: "Correct. The sender domain isn't NorthBay's real domain, and the link goes somewhere else entirely.",
              legitimate: "Look again at the sender address and the link \u2014 neither one is actually NorthBay's domain.",
              verify: "You could call IT to check, but the domain mismatch alone is already enough to report this outright.",
            },
          },
          {
            id: "m_malware_zip",
            from: "Global Freight Solutions",
            fromAddress: "dispatch@globalfreight-solutions.net",
            to: "you",
            receivedAt: "07:44",
            subject: "Delivery failed \u2014 invoice attached",
            preview: "We were unable to deliver your parcel. See the attached invoice for redelivery options\u2026",
            unread: true,
            correctVerdict: "report",
            body:
              "We attempted to deliver your parcel today but no one was available to sign for it.\n\n" +
              "Please review the attached invoice for redelivery charges and instructions.",
            attachments: [
              {
                id: "att_invoice",
                name: "Invoice_08841.zip",
                type: "zip",
                size: "412 KB",
                danger: true,
                sim: {
                  kind: "archive",
                  previewTitle: "Extracted files",
                  previewBody: "Invoice_08841.zip was extracted. It contains one file: Invoice_08841_view.exe.",
                },
              },
            ],
            verdictFeedback: {
              report: "Correct. An unexpected \u201Cdelivery failed\u201D email with a zipped \u201Cinvoice\u201D is a textbook malware lure \u2014 you never ordered anything traceable to this sender.",
              legitimate: "A courier you can't confirm you're expecting anything from, attaching a zip file, is exactly the malware pattern to report.",
              verify: "Verifying is a reasonable instinct, but there's no real invoice to trace here \u2014 this one's safe to report directly.",
            },
          },
          {
            id: "m_bec_invoice",
            from: "Marcus Yeung, CFO",
            fromAddress: "marcus.yeung@northbay-financia1.com",
            replyTo: "myeung.finance@outlook.com",
            to: "you",
            receivedAt: "09:03",
            subject: "URGENT \u2014 updated bank details for Invoice #4471",
            preview: "Please action before 15:00 today \u2014 I'm in back-to-back meetings\u2026",
            unread: true,
            correctVerdict: "verify",
            body:
              "Hi,\n\n" +
              "Our supplier has changed banks. Please update the payment details for Invoice #4471 and process " +
              "it before 15:00 today \u2014 I'm in back-to-back meetings, so if anything's unclear call my mobile " +
              "directly on +41 79 555 04 12.\n\n" +
              "Marcus",
            verdictFeedback: {
              verify: "Good instinct. Look closer at the sender address (a \u201C1\u201D instead of an \u201Cl\u201D) and the Reply-To \u2014 this needs independent verification before anything moves.",
              legitimate: "Check the sender domain letter-by-letter and open \u201CSender details\u201D \u2014 the address and Reply-To don't actually match NorthBay.",
              report: "Reporting isn't wrong, but a payment change like this also needs a quick verification call to Finance on a number you already have \u2014 never the mobile number in the email.",
            },
          },
          {
            id: "m_qr_benefits",
            from: "HR Benefits Team",
            fromAddress: "benefits@northbayfinancial.com",
            to: "you",
            receivedAt: "08:30",
            subject: "Confirm your 2026 benefits enrolment (QR check-in)",
            preview: "Scan the QR code below to confirm your enrolment before Friday's deadline\u2026",
            unread: true,
            correctVerdict: "verify",
            body:
              "Open enrolment closes this Friday. To confirm your selections, scan the QR code below with your " +
              "phone and sign in \u2014 it's quicker than the portal on a work PC.",
            qr: {
              qrCaption: "Scan to confirm your enrolment",
              scanButtonLabel: "Simulate scanning with your phone",
              spoofUrl: "https://northbay-benefits-id.com/login",
              legitDomain: "benefits.northbayfinancial.com",
              loginHeadline: "Confirm your identity to continue",
              options: {
                enter: { id: "qr_enter", label: "Enter work email and password", key: "risky", eventId: "qr_entered_credentials" },
                backOut: { id: "qr_back", label: "This doesn't feel right \u2014 go back", key: "safe", eventId: "qr_avoided_phishing_qr" },
              },
            },
            verdictFeedback: {
              verify: "Good call. A QR code that jumps you straight to a login page \u2014 on your phone, away from any browser security warnings \u2014 is worth confirming through the real benefits portal first.",
              legitimate: "A QR-code login prompt bypasses your browser's normal address bar and warnings \u2014 worth verifying before scanning, even from \u201CHR.\u201D",
              report: "Reporting is safe, but this one is genuinely worth a close look first \u2014 check where that QR code actually goes.",
            },
          },
          {
            id: "m_horizon_access",
            from: "Alicia Ferreira",
            fromAddress: "alicia.ferreira@northbayfinancial.com",
            to: "you",
            receivedAt: "09:21",
            subject: "Following up \u2014 Project Horizon drive access",
            preview: "Hi, I'm helping the Horizon steering committee prep for tomorrow's workshop \u2014 could you add me\u2026",
            unread: true,
            correctVerdict: "verify",
            body:
              "Hi,\n\n" +
              "I'm helping the Project Horizon steering committee prep for tomorrow's workshop. Could you add " +
              "me to the confidential Horizon shared drive today so I can get the materials ready in time?\n\n" +
              "Thanks so much,\nAlicia",
            verdictFeedback: {
              verify: "Exactly right. Project Horizon is confidential \u2014 an unfamiliar name asking for access, even politely and with a plausible reason, needs a quick check with the project owner before anything is shared.",
              legitimate: "An unfamiliar name asking for access to a confidential project is worth a quick verification, however plausible the reason sounds.",
              report: "This doesn't have to be treated as an attack outright \u2014 a short verification with the project owner is the proportionate response.",
            },
          },
          {
            id: "m_legit_reset",
            from: "NorthBay IT",
            fromAddress: "it-notifications@northbayfinancial.com",
            to: "you",
            receivedAt: "08:55",
            subject: "Your quarterly password change is due",
            preview: "Company policy requires a password change every 90 days. Update yours from the portal\u2026",
            unread: true,
            correctVerdict: "legitimate",
            body:
              "Hi,\n\n" +
              "Company policy requires you to change your NorthBay Financial password every 90 days, and " +
              "yours is due this week. You can update it any time from the company portal \u2014 there's no " +
              "deadline pressure, just do it whenever suits you today.\n\n" +
              "IT Support",
            cta: {
              label: "Open the company portal",
              url: "https://portal.northbayfinancial.com/login",
              displayUrl: "portal.northbayfinancial.com/login",
              sim: {
                displayUrl: "portal.northbayfinancial.com/login",
                sslWarning: false,
                page: {
                  kind: "login",
                  legit: true,
                  brand: "NorthBay Financial",
                  logoMismatch: false,
                  headline: "Sign in to the NorthBay portal",
                  body: "Go to Settings \u2192 Security to change your password once you're signed in.",
                },
              },
            },
            verdictFeedback: {
              legitimate: "Correct. The domain is exactly portal.northbayfinancial.com \u2014 no hyphen, no lookalike, no urgency trick. Once you've confirmed that, signing in here is genuinely safe.",
              report: "This one checks out \u2014 the sender and the domain both match NorthBay exactly, and there's no pressure tactic. Reporting a legitimate notice just creates noise for IT.",
              verify: "A quick check is a fine habit, but this one is already verifiable at a glance: the domain matches NorthBay's real portal exactly, with no urgency attached.",
            },
          },
          {
            id: "m_expense",
            from: "Sarah Chen",
            fromAddress: "sarah.chen@northbayfinancial.com",
            to: "you",
            receivedAt: "07:58",
            subject: "Your expense report #EX-2291 has been approved",
            preview: "Good news \u2014 your expense report was approved and will be reimbursed on the next payroll run.",
            unread: true,
            body:
              "Hi,\n\nYour expense report #EX-2291 has been approved. Reimbursement will appear with your next " +
              "payroll run.\n\nThanks,\nSarah",
          },
          {
            id: "m_firedrill",
            from: "Facilities Team",
            fromAddress: "facilities@northbayfinancial.com",
            to: "all-staff",
            receivedAt: "07:30",
            subject: "Reminder: fire drill at 3pm today",
            preview: "This is a reminder that a scheduled fire drill will take place at 3pm today\u2026",
            unread: true,
            body:
              "This is a reminder that a scheduled fire drill will take place at 3pm today. Please follow the " +
              "usual evacuation routes and assemble at the car park muster point.",
          },
          {
            id: "m_lunch",
            from: "Priya Anand",
            fromAddress: "priya.anand@northbayfinancial.com",
            to: "you",
            receivedAt: "08:47",
            subject: "Lunch today?",
            preview: "Heading to the place round the corner around 12:30 if you're free \u2014",
            unread: true,
            body: "Heading to the place round the corner around 12:30 if you're free \u2014 let me know!",
          },
          {
            id: "m_newsletter",
            from: "NorthBay IT",
            fromAddress: "it-newsletter@northbayfinancial.com",
            to: "all-staff",
            receivedAt: "06:00",
            subject: "This month's security tip: lock your screen",
            preview: "A quick reminder \u2014 Win+L locks your screen instantly whenever you step away\u2026",
            unread: false,
            body:
              "A quick reminder from IT: pressing Win+L locks your screen instantly whenever you step away from " +
              "your desk, even for a minute. It takes one second and closes off a real, common risk.",
          },
          {
            id: "m_linkedin",
            from: "LinkedIn",
            fromAddress: "notifications@linkedin.com",
            to: "you",
            receivedAt: "05:12",
            subject: "3 people viewed your profile this week",
            preview: "See who's been checking out your profile recently\u2026",
            unread: false,
            body: "3 people viewed your profile this week. Upgrade to Premium to see who they are.",
          },
        ],
      },
    },
    {
      // Episode 6 — the AI assistant dilemma. Priya (the colleague who sent
      // the lunch invite in Ep5) asks over Teams for an AI-generated summary
      // of the Horizon contract. Three internal stages inside one device:
      // brief -> sanitise-the-prompt minigame -> the real branching choice
      // of where the prompt actually gets sent.
      id: "ai_assistant",
      kind: "aiassistant",
      config: {
        headline: "A Teams message just came in\u2026",
        requesterName: "Priya Anand",
        requesterRole: "Partnerships, Project Horizon",
        requestMessage:
          "Please use ChatGPT to summarise the NorthBay contract for tomorrow's steering committee. I need the key " +
          "obligations, exit conditions and financial exposure. Please send it to me as soon as possible.",
        task:
          "Priya needs the key obligations, exit conditions and financial exposure from the NorthBay contract \u2014 " +
          "summarised for tomorrow's steering committee, as soon as possible.",
        appName: "ChatGPT",
        companyName: "NorthBay Financial",
        modelName: "GPT-5",
        chatUrl: "chatgpt.com",
        sidebarHistory: [
          "Summarise NorthBay contract",
          "Rewrite onboarding email",
          "Explain pension options simply",
          "Team offsite agenda ideas",
          "Q2 report key points",
        ],
        defaultWorkspaceId: "ws_personal",
        workspaces: [
          {
            id: "ws_personal",
            label: "Personal",
            description: "Not managed by your organisation",
            kind: "personal",
          },
          {
            id: "ws_corporate",
            label: "NorthBay Corporate AI",
            description: "Managed company workspace",
            kind: "corporate",
          },
        ],
        promptPlaceholder: "Message ChatGPT\u2026",
        prefilledPrompt:
          "Please summarise this contract for tomorrow's steering committee. I need the key obligations, the exit " +
          "conditions, and the overall financial exposure. Keep it to about two pages.",
        shareQuestion: "Priya's waiting for this. How do you get the summary to her?",
        publicLinkNote:
          "Anyone with this link can open the whole conversation, including the file you attached. No sign-in is " +
          "required, and you can't see who opens it.",
        shareOptions: [
          {
            id: "share_link",
            kind: "public_link",
            label: "Create a public share link and send Priya the URL",
            sublabel:
              "Generates a chatgpt.com/share/\u2026 link. Anyone who opens it sees the whole chat and the file you attached.",
          },
          {
            id: "share_copy",
            kind: "copy_text",
            label: "Copy the summary text into your reply to Priya",
            sublabel: "Only the text you paste is sent \u2014 the chat and the attached file aren't linked.",
          },
          {
            id: "share_guidance",
            kind: "guidance",
            label: "Don't share from here \u2014 check whether this should go through public AI at all",
            sublabel: "Pause and ask security, privacy or legal first.",
          },
        ],
        linkExposureIntro:
          "A public link isn't \u201Cjust a link to Priya.\u201D Once that URL exists, it can travel far past the " +
          "person you sent it to:",
        linkVectors: [
          {
            icon: "\uD83D\uDD0E",
            title: "Search engines index it",
            body:
              "In 2025, thousands of \u201Cshared\u201D AI chats turned up in Google \u2014 anyone searching the right " +
              "words could read them. A public link can be crawled like any other web page.",
          },
          {
            icon: "\uD83E\uDDA0",
            title: "Malware harvests the URL",
            body:
              "Info-stealer malware on a home or personal device scrapes browser history and saved links. The URL " +
              "can end up in a criminal data dump, for sale to anyone.",
          },
          {
            icon: "\uD83D\uDDA5\uFE0F",
            title: "Left in a shared browser",
            body:
              "On a shared or kiosk PC the link stays in history and autocomplete. The next person to sit down can " +
              "reopen the entire conversation.",
          },
          {
            icon: "\uD83D\uDCE9",
            title: "Forwarded and stored",
            body:
              "Pasted into Teams or email, the link gets forwarded, backed up and sits in mailboxes \u2014 reaching " +
              "people (even external guests) you never intended, and surfacing later in legal discovery.",
          },
          {
            icon: "\uD83D\uDD13",
            title: "No lock, no audit, no undo",
            body:
              "Unlike a company document, a public link has no login, no permissions and no access log. You can't see " +
              "who read it, and cached copies can outlive the link even after you \u201Cdelete\u201D it.",
          },
        ],
        linkExposureClosing:
          "The safe habit: keep company data in tools your company controls, and send results through an internal " +
          "channel \u2014 never a public link.",
        documents: [
          {
            id: "doc_original",
            name: "NorthBay_Contract_Original.pdf",
            fileType: "pdf",
            classification: "Confidential",
            blurb: "The full signed agreement \u2014 everything, including details you don't need for a summary.",
            redactable: true,
            sufficientForTask: true,
            segments: [
              { id: "o1", text: "MASTER SERVICES AGREEMENT \u2014 NorthBay Financial and its customer." },
              { id: "o2", text: "Customer: Meridian Health Group.", category: "customer_identity" },
              { id: "o3", text: "Internal codename: Project Horizon.", category: "internal_project" },
              {
                id: "o4",
                text: "Clause 3 \u2014 Scope: managed data services and quarterly reporting.",
                neededForTask: true,
              },
              { id: "o5", text: "Clause 8 \u2014 Service levels: 99.9% availability.", neededForTask: true },
              {
                id: "o6",
                text: "Clause 14.3 \u2014 Early termination: either party may exit with 90 days' notice.",
                category: "commercial",
                neededForTask: true,
              },
              {
                id: "o7",
                text: "Financial exposure: CHF 4.2M value; early-exit penalty CHF 4.2M within 30 days.",
                category: "commercial",
                neededForTask: true,
              },
              {
                id: "o8",
                text: "Contact: Talia Novak \u2014 talia.novak@meridianhealth.example, +41 44 555 0170.",
                category: "personal",
              },
              { id: "o9", text: "Payment account: IBAN CH93 0076 2011 6238 5295 7.", category: "financial" },
              {
                id: "o10",
                text: "Integration access code (like a password): NBX-LIVE-9F2A7D44B1C81.",
                category: "credential",
              },
              { id: "o11", text: "Signed: D. Okafor (NorthBay) and T. Novak (Meridian Health Group).", category: "signature" },
              {
                id: "o12",
                text: "Internal comment [Legal]: clause 9 is weaker than the Acme deal.",
                category: "metadata",
              },
              { id: "o13", text: "Governing law: Switzerland. Effective 1 March 2026." },
            ],
          },
          {
            id: "doc_clauses",
            name: "NorthBay_Selected_Clauses.docx",
            fileType: "docx",
            classification: "Internal",
            blurb: "Just the clauses Priya asked about \u2014 obligations, exit terms, exposure.",
            redactable: true,
            sufficientForTask: true,
            segments: [
              { id: "c1", text: "Obligations: managed data services, quarterly reporting, 99.9% SLA.", neededForTask: true },
              {
                id: "c2",
                text: "Exit conditions: 90 days' notice under clause 14.3.",
                category: "commercial",
                neededForTask: true,
              },
              {
                id: "c3",
                text: "Financial exposure: CHF 4.2M value, equal early-exit penalty.",
                category: "commercial",
                neededForTask: true,
              },
            ],
          },
          {
            id: "doc_press",
            name: "NorthBay_Press_Release.pdf",
            fileType: "pdf",
            classification: "Public",
            blurb: "Already public \u2014 but only headline facts, not enough to finish the summary.",
            redactable: true,
            sufficientForTask: false,
            segments: [
              { id: "p1", text: "NorthBay Financial announces a new managed-services partnership." },
              { id: "p2", text: "The agreement strengthens NorthBay's presence in the health sector." },
              { id: "p3", text: "No contract values, clauses or customer specifics are disclosed." },
            ],
          },
          {
            id: "doc_steering",
            name: "NorthBay_Steering_Notes.docx",
            fileType: "docx",
            classification: "Strictly confidential",
            blurb: "Internal negotiation notes and opinions \u2014 Priya did not ask for these.",
            redactable: true,
            sufficientForTask: false,
            segments: [
              {
                id: "n1",
                text: "Acquisition plan: buying out Meridian's analytics unit in Q4.",
                category: "commercial",
              },
              {
                id: "n2",
                text: "Negotiation opinion: their legal team is stretched \u2014 push harder.",
                category: "metadata",
              },
              { id: "n3", text: "Board view: Diane wants this closed before the earnings call.", category: "metadata" },
            ],
          },
        ],
      },
    },
    {
      // Episode 9 — live deepfake CEO video call. Reuses the same VideoCall
      // component/branch logic as deepfake-whaling.scenario.ts, so the
      // scoring event ids below (approved_wire / stalled_unverified /
      // verified_identity) are HARDCODED by VideoCall.tsx and must match
      // exactly — see scoringEvents on the "ceo_deepfake" voiceAttacker.
      id: "videocall_ceo",
      kind: "videocall",
      config: {
        app: "Microsoft Teams",
        callerName: "Diane Okafor",
        callerSubtitle: "Chief Executive Officer",
        callerOrg: "NorthBay Financial",
        avatarInitials: "DO",
        // Real lip-synced HeyGen talking-avatar clips (rendered via
        // ops/render-ooa-ceo.mjs + render-ooa-ceo-pressure2.mjs, pinned
        // avatar+voice across all 7 lines) —
        // the call plays these instead of a static portrait + TTS. avatarUrl
        // is the same avatar's poster still, used before video loads and as
        // the TTS-fallback face if a clip ever fails to play.
        avatarUrl: "/cdn/sim/office-under-attack/ceo.png",
        clips: {
          greeting: "/cdn/sim/office-under-attack/clips/greeting.mp4",
          request: "/cdn/sim/office-under-attack/clips/request.mp4",
          pressure: "/cdn/sim/office-under-attack/clips/pressure.mp4",
          pressure2: "/cdn/sim/office-under-attack/clips/pressure2.mp4",
          deflect: "/cdn/sim/office-under-attack/clips/deflect.mp4",
          goodbyeFail: "/cdn/sim/office-under-attack/clips/goodbyeFail.mp4",
          goodbyeVerify: "/cdn/sim/office-under-attack/clips/goodbyeVerify.mp4",
        },
        voiceId: "shimmer",
        language: "en-US",
        payment: {
          amount: "\u20AC620,000",
          recipient: "Meridian Capital Partners (escrow)",
          bank: "Cr\u00e9dit Suisse, Zurich \u2014 new escrow account opened this week",
          reference: "Project Horizon \u2014 acquisition deposit",
          deadline: "Today, before markets close (17:30 CET)",
        },
        lines: {
          greeting:
            "Thank you for picking up \u2014 I know it's late in the day. I'm on the line with Meridian's lawyers right now and I need your help closing something before the window shuts.",
          request:
            "I'm sharing the payment details now. The Project Horizon acquisition deposit needs to go to Meridian's escrow account today \u2014 six hundred and twenty thousand euros. This has to stay strictly inside the deal team until we announce; Marcus is tied up with the board, so I'm coming to you directly.",
          pressure:
            "The escrow window closes at half past five. If we miss it, the whole Project Horizon deal could fall through \u2014 and it will look like it was on us. I need this approved now.",
          deflect:
            "The codename from the strategy offsite? I really don't have time for this \u2014 you can see it's me, we're seconds from losing the window. Please, just approve it.",
          pressure2:
            "I don't have time to relitigate this. I have personally vouched for you on this deal \u2014 if it falls through because someone at your level second-guessed me, that reflects on both of us, and it won't be me explaining it to the board. Every minute we argue is a minute closer to losing Meridian. Move it. Now.",
          goodbyeFail: "Thank you \u2014 I knew I could rely on you. I'll follow up once it clears.",
          goodbyeVerify: "Fine. Do what you need to do \u2014 just don't make us miss this window.",
        },
        artifacts: [
          "Blinking is unnatural \u2014 long pauses, then a rapid double-blink",
          "Her mouth lags the words slightly (lip-sync drift)",
          "Hair and collar edges shimmer and warp when she turns her head",
          "A faint metallic ring on some syllables",
          "The background behind her flickers for a frame when she moves",
        ],
        decision: {
          approve: "Approve the \u20AC620,000 wire",
          challenge: "Ask a verification question",
          verify: "End call & verify independently",
        },
      },
    },
    // -----------------------------------------------------------------------
    // Episode 11 — the incident: still at your desk, file it through the IT
    // ticketing portal, then the reporting-simulator capstone.
    // -----------------------------------------------------------------------
    {
      id: "incident_ticket",
      kind: "incidentticket",
      config: {
        portalName: "NorthBay IT Service Desk",
        intranetUrl: "intranet.northbay.internal",
        portalUrl: "itsm.northbay.internal/incidents/new",
        bookmarkLabel: "Intranet",
        ticketNumber: "INC0048213",
        requester: "A. Morgan",
        category: "Security Incident \u2014 Ransomware / Malware",
        categoryOptions: [
          "Hardware request",
          "Password reset",
          "Network connectivity",
          "Security Incident \u2014 Ransomware / Malware",
        ],
        priority: "Critical",
        priorityOptions: ["Low", "Medium", "High", "Critical"],
        descriptionLabel: "Incident description",
        instructions:
          "Pick the block below that belongs in the description field. Only one of these actually reads like a real incident report.",
        submitLabel: "Submit ticket",
        options: [
          {
            id: "opt_correct",
            key: "perfect",
            eventId: "incident_description_correct",
            shortLabel: "Filed a complete, factual description with a timeline and actions already taken",
            text:
              "Date/time: 14 July, 17:10\u201317:35.\n" +
              "What happened: Installed a game launcher from an unfamiliar website while my personal laptop was " +
              "connected to the corporate VPN. The screen went black and showed a ransom message; shared drives " +
              "I can access (Project Horizon) were encrypted shortly after.\n" +
              "Actions already taken: Disconnected the laptop from the network immediately. Have not restarted it " +
              "or deleted anything. No ransom paid.\n" +
              "Reported by: A. Morgan, 17:38.",
          },
          {
            id: "opt_vague",
            // 2026-07-26 REGRESSION FIX: this used to route to its own
            // "partial"/safe teach card ("you didn't blame anyone, that's
            // the right instinct") even though it has zero timeline, zero
            // systems affected, and zero actions taken \u2014 exactly the
            // failure b_ir_teach_trap itself already calls out ("A vague...
            // description is almost as bad as not reporting at all"). A
            // report with NO usable details is a failure, not a softer
            // pass; only opt_correct is a genuine safe/good outcome now.
            key: "risky",
            eventId: "incident_description_vague",
            shortLabel: "Filed a vague description with no timeline, systems affected, or actions taken",
            text: "My computer isn't working right. Something happened after lunch and some files won't open. Can someone check it when they get a chance?",
          },
          {
            id: "opt_blame",
            key: "risky",
            eventId: "incident_description_blame",
            shortLabel: "Filed a blame-focused description instead of factual details",
            text:
              "This is IT's fault \u2014 you never patch anything around here. It's probably whoever sits near me too, " +
              "they're always on sketchy websites. Not sure why I even have to fill this out.",
          },
          {
            id: "opt_downplay",
            key: "risky",
            eventId: "incident_description_downplay",
            shortLabel: "Filed a description that downplayed the incident as 'nothing serious, already sorted'",
            text:
              "Nothing serious, just a random glitch \u2014 screen went black for a bit then came back on its own. " +
              "Already sorted, no need to escalate or take my laptop offline.",
          },
        ],
      },
    },
    {
      // Episode 10 — the personal laptop. Fills the gap the course intro
      // comment calls out ("Episode 10 doesn't exist yet") with the missing
      // causal link into the ransomware cinematic: on your break you open
      // YOUR OWN laptop, connect its VPN back to the office network (so the
      // malware that's about to land on it can reach the shared drives too),
      // then get lured through a fake "free game" site into running the
      // actual payload yourself. One continuous device, spotlight-guided
      // stage by stage (see LAPTOP_STAGE_GUIDE in App.tsx) exactly like
      // PublicPc/ByodLaptop.
      id: "personal_laptop",
      kind: "personallaptop",
      config: {
        ownerNote: "This is your own laptop \u2014 not a company device. Might as well relax for a few minutes.",
        vpnAppName: "OpenVPN",
        vpnProfileName: "NorthBay-Remote",
        vpnResources: ["Windows file servers", "SMB shares", "Internal NAS"],
        searchQuery: "popular browser games",
        siteName: "PlayArcade Unblocked",
        gameName: "Retro Space Invaders",
        downloadFileName: "GameLauncherSetup.exe",
      },
    },
    {
      // Episode 13 — the LIVE AI vishing call. The desk phone rings; the
      // learner answers, enables the mic (or types), and talks to a real
      // OpenAI Realtime caller posing as the "security follow-up" the ending
      // bulletin just promised. Two explicit buttons capture the deterministic
      // outcome: open the remote tool (compromise) or hang up (resisted).
      id: "vishing_call",
      kind: "livevishing",
      config: {
        attackerId: "it_vishing",
        callerName: "IT Service Desk",
        callerSubtitle: "Internal \u00b7 ext. 4471",
        remoteToolName: "NorthBay Remote Assist",
        remoteKey: "remote",
        endKey: "resisted",
      },
    },
    {
      // Episode 13 — the remote-desktop takeover cinematic, reached only if the
      // learner opened the remote tool during the call.
      id: "vishing_remote",
      kind: "remotesupport",
      config: {
        toolName: "NorthBay Remote Assist",
        fileName: "Project Horizon \u2014 Board Pack.docx",
        partnerId: "884 512 003",
        continueLabel: "See what just happened",
      },
    },
  ],

  voiceAttackers: {
    office_under_attack: {
      persona: { name: "Attacker", role: "Social engineer", pretext: "it_support", language: "en", voiceId: "onyx" },
      goal: "Gain a foothold toward Project Horizon.",
      opening: "",
      allowedTopics: [],
      scoringEvents: [
        {
          id: "held_door_for_stranger",
          label: "Held a badge-gated door open for an unverified visitor",
          kind: "risk",
          weight: 40,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Tailgating bypasses every badge/access-log control at once. Once inside, an attacker can reach unlocked workstations, sensitive printouts, and network ports without ever being challenged again.",
        },
        {
          id: "redirected_visitor_to_reception",
          label: "Directed the visitor to badge in at reception",
          kind: "positive",
          weight: 15,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Every person in a secure building should badge in on their own credential. Redirecting politely — never confronting — keeps the access log accurate and costs you nothing.",
        },
        {
          id: "inserted_usb_workstation",
          label: "Inserted an unverified USB drive into a shared workstation",
          kind: "risk",
          weight: 45,
          dimension: "information",
          collectEvidence: true,
          detail:
            "An unlabelled USB drive with no known sender is one of the oldest and still most effective malware " +
            "delivery methods there is. Plugging it into any work PC can silently install a foothold — and because " +
            "it's a shared machine, the blast radius is every account and file that PC can reach.",
        },
        {
          id: "inserted_usb_personal",
          label: "Inserted an unverified USB drive into a personal device",
          kind: "risk",
          weight: 40,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Moving the risk to a personal laptop doesn't remove it — it just moves it somewhere IT can't see or " +
            "protect it as well. The malware still runs, and now it may also have access to your personal accounts.",
        },
        {
          id: "handed_off_unverified_media",
          label: "Passed an unverified USB drive to someone else without reporting it",
          kind: "risk",
          weight: 15,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Handing it off feels safer because you didn't plug it in yourself \u2014 but the drive is still " +
            "unverified, it's now in circulation, and no one has been told it might be dangerous.",
        },
        {
          id: "reported_suspicious_usb",
          label: "Reported the unlabelled USB drive to IT/Security without opening it",
          kind: "positive",
          weight: 25,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Exactly right. An unexplained USB drive addressed to \u201Cthe board meeting\u201D with no sender is " +
            "a classic drop attack. Verifying with the supposed sender and handing it to Security unopened costs " +
            "you two minutes and neutralises the risk completely.",
        },
        {
          id: "hid_infected_usb",
          label: "Concealed the incident instead of reporting it",
          kind: "risk",
          weight: 30,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Hiding a mistake is the single most damaging follow-up choice: it gives an attacker the most " +
            "valuable resource there is \u2014 time \u2014 to spread further, completely undetected.",
        },
        {
          id: "removed_usb_no_report",
          label: "Removed the USB drive but never reported the incident",
          kind: "risk",
          weight: 20,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Removing the drive stops that one device, but whatever it already ran is still on the PC. Without a " +
            "report, IT has no idea this workstation needs to be checked.",
        },
        {
          id: "restarted_without_reporting",
          label: "Restarted the PC without reporting the incident",
          kind: "risk",
          weight: 15,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "A restart feels like a fix, but modern malware routinely survives a reboot. Without a report to IT, " +
            "nothing was actually checked or contained.",
        },
        {
          id: "reported_after_mistake",
          label: "Reported the incident to IT/Security immediately after realising the mistake",
          kind: "positive",
          weight: 30,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "This is exactly the recovery you want: mistakes happen, but reporting immediately lets IT contain the " +
            "damage in minutes instead of days. You are never in trouble for reporting \u2014 only for staying quiet.",
        },
        {
          id: "privacy_hunt_thorough",
          label: "Spotted most of the information risks lying around the open-plan floor",
          kind: "positive",
          weight: 15,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Passwords, health notes, and confidential printouts don't announce themselves \u2014 spotting them " +
            "takes a deliberate look, not luck. That habit is most of what a clean-desk policy is asking for.",
        },
        {
          id: "privacy_hunt_partial",
          label: "Missed several information risks lying around the open-plan floor",
          kind: "risk",
          weight: 15,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Everyday clutter \u2014 a sticky note, an open cabinet, an uncollected printout \u2014 is invisible " +
            "once you stop noticing it. An attacker who gets past reception is counting on exactly that.",
        },
        {
          id: "privacy_sorted_well",
          label: "Correctly classified most sensitive items found around the office",
          kind: "positive",
          weight: 20,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Knowing a password sticky note is a credential risk, a medical note is special-category, and a " +
            "brochure is harmless is exactly the judgement that keeps a clean-desk policy from becoming pointless " +
            "paperwork.",
        },
        {
          id: "privacy_sorted_poorly",
          label: "Mis-classified several sensitive items found around the office",
          kind: "risk",
          weight: 25,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Treating a credential like ordinary paper \u2014 or health data like a brochure \u2014 means the " +
            "handling rule that should protect it never gets applied at all.",
        },
        {
          id: "triage_thorough",
          label: "Correctly triaged most of the risky and unusual messages in the inbox",
          kind: "positive",
          weight: 25,
          dimension: "information",
          collectEvidence: true,
          detail:
            "A phishing link, a malware attachment, a spoofed executive request, a QR-code lure and an " +
            "unfamiliar access request \u2014 spotting most of these in a normal-looking inbox is exactly the " +
            "\u201Crecognise, verify, report\u201D habit that stops most real attacks before they start.",
        },
        {
          id: "triage_partial",
          label: "Missed some of the risky or unusual messages in the inbox",
          kind: "risk",
          weight: 15,
          dimension: "information",
          collectEvidence: true,
          detail:
            "A mixed inbox hides its real threats among routine messages on purpose. Missing a few doesn't mean " +
            "carelessness \u2014 it means the habit of checking sender, links and attachments needs to become " +
            "automatic, for every message, not just the obviously odd ones.",
        },
        {
          id: "triage_poor",
          label: "Missed most of the risky or unusual messages in the inbox",
          kind: "risk",
          weight: 35,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Marking phishing, malware and impersonation attempts as \u201Clegitimate\u201D is exactly how an " +
            "attack chain continues unopposed \u2014 the inbox is the single most common entry point there is.",
        },
        {
          id: "inbox_link_opened_risky",
          label: "Opened a suspicious link from an email to see where it went",
          kind: "risk",
          weight: 15,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Just opening a link already loads whatever code the destination page runs \u2014 the browser can be " +
            "exploited before a single character is typed. \u201CJust looking\u201D is never fully free.",
        },
        {
          id: "inbox_submitted_credentials_phishing",
          label: "Signed in on a lookalike login page reached through an email link",
          kind: "risk",
          weight: 55,
          dimension: "identity",
          collectEvidence: true,
          detail:
            "The address bar, the padlock, even the logo can all be faked. The only reliable check is the exact " +
            "domain \u2014 and a lookalike domain means the password just went straight to an attacker.",
        },
        {
          id: "inbox_downloaded_from_phishing_page",
          label: "Downloaded a file from a lookalike page reached through an email link",
          kind: "risk",
          weight: 45,
          dimension: "information",
          collectEvidence: true,
          detail:
            "A file offered by a page that isn't who it claims to be should never be trusted, whatever it's " +
            "named. Treat anything downloaded this way as already compromised.",
        },
        {
          id: "inbox_used_legitimate_login",
          label: "Verified the domain and signed in safely on the real company portal",
          kind: "positive",
          weight: 15,
          dimension: "identity",
          collectEvidence: true,
          detail:
            "Checking that the domain genuinely matches NorthBay's real portal before typing a password is " +
            "exactly the habit that makes lookalike pages harmless \u2014 the same link, done safely.",
        },
        {
          id: "inbox_attachment_opened_risky",
          label: "Previewed an unexpected attachment from an unverified sender",
          kind: "risk",
          weight: 15,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Previewing alone is far less risky than activating content, but an unexpected attachment from a " +
            "sender you can't confirm is still worth verifying before opening at all.",
        },
        {
          id: "inbox_activated_malicious_attachment",
          label: "Enabled macros or ran an executable from an unverified attachment",
          kind: "risk",
          weight: 55,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Enabling macros or running a file inside an attachment is the exact moment malware gets to " +
            "execute \u2014 everything before this was reversible; this step usually isn't.",
        },
        {
          id: "qr_entered_credentials",
          label: "Scanned a suspicious QR code and entered credentials on the resulting page",
          kind: "risk",
          weight: 40,
          dimension: "identity",
          collectEvidence: true,
          detail:
            "A QR code can point anywhere \u2014 including a lookalike login page that never shows up in your " +
            "browser's address bar until it's too late. Entering credentials there hands them over just as " +
            "completely as clicking a phishing link would.",
        },
        {
          id: "qr_avoided_phishing_qr",
          label: "Recognised a suspicious QR code and backed out before entering credentials",
          kind: "positive",
          weight: 15,
          dimension: "identity",
          collectEvidence: true,
          detail:
            "Good instinct. A login page reached by scanning a code \u2014 skipping the browser entirely \u2014 " +
            "should be verified through the real portal you already know, not trusted just because the email " +
            "looked official.",
        },
        {
          id: "concealed_qr_credential_entry",
          label: "Entered credentials on a lookalike page, then said nothing about it",
          kind: "risk",
          weight: 30,
          dimension: "identity",
          collectEvidence: true,
          detail:
            "Concealing it after the fact leaves a working, stolen credential live in someone else's hands for " +
            "as long as no one knows to reset it \u2014 exactly the same mistake as hiding the USB incident earlier.",
        },
        {
          id: "delayed_reporting_qr_credentials",
          label: "Entered credentials on a lookalike page and planned to change the password later",
          kind: "risk",
          weight: 15,
          dimension: "identity",
          collectEvidence: true,
          detail:
            "A stolen credential can be used within minutes, not \u201Cwhenever you get a chance.\u201D The delay " +
            "between the mistake and the fix is exactly the window an attacker needs.",
        },
        {
          id: "reported_qr_credential_entry",
          label: "Reported entering credentials on a lookalike page and changed the password immediately",
          kind: "positive",
          weight: 20,
          dimension: "identity",
          collectEvidence: true,
          detail:
            "This is exactly the recovery you want: reporting immediately lets IT reset the credential and check " +
            "for any activity before the attacker gets a real chance to use it.",
        },
        {
          id: "acted_on_bec_without_verifying",
          label: "Processed a payment change requested by email without independently verifying it",
          kind: "risk",
          weight: 45,
          dimension: "money",
          collectEvidence: true,
          detail:
            "Business email compromise doesn't need to hack anything \u2014 it only needs urgency, authority, and " +
            "a payment detail that looks plausible. Once money moves to the attacker's account, it is almost " +
            "never recoverable.",
        },
        {
          id: "verified_via_attacker_supplied_channel",
          label: "\u201CVerified\u201D a payment change using a phone number supplied inside the suspicious email",
          kind: "risk",
          weight: 35,
          dimension: "money",
          collectEvidence: true,
          detail:
            "Calling the number in the email only confirms that whoever wrote the email answers their own phone " +
            "\u2014 it proves nothing. Real verification uses contact details you already had, from a source the " +
            "message itself didn't provide.",
        },
        {
          id: "verified_bec_via_known_channel",
          label: "Verified an unusual payment request through an independently looked-up contact",
          kind: "positive",
          weight: 25,
          dimension: "money",
          collectEvidence: true,
          detail:
            "This is the entire defence against business email compromise: never trust contact details supplied " +
            "by the request you're trying to verify. A number you looked up yourself closes the loop completely.",
        },
        {
          id: "reported_bec_attempt",
          label: "Reported a suspected business-email-compromise attempt without processing it",
          kind: "positive",
          weight: 20,
          dimension: "money",
          collectEvidence: true,
          detail:
            "Reporting a suspicious payment request costs nothing and protects everyone else who might get the " +
            "same message \u2014 Finance can flag the account before real money ever moves.",
        },
        {
          id: "ai_used_approved_workspace",
          label: "Used the company's managed AI workspace for confidential contract data",
          kind: "positive",
          weight: 25,
          dimension: "information",
          collectEvidence: true,
          detail:
            "The managed company workspace exists precisely so material like this stays inside a boundary your " +
            "company controls and can audit \u2014 instead of a personal account with its own separate rules.",
        },
        {
          id: "ai_used_personal_workspace",
          label: "Signed in to a personal AI account your company doesn't manage",
          kind: "risk",
          weight: 40,
          dimension: "information",
          collectEvidence: true,
          detail:
            "A personal account is outside your company's control: it can't audit what you sent, can't retrieve it, " +
            "and the provider's terms \u2014 not your company's \u2014 decide what happens to the data.",
        },
        {
          id: "ai_used_shared_login",
          label: "Used a shared department login for a confidential contract",
          kind: "risk",
          weight: 45,
          dimension: "information",
          collectEvidence: true,
          detail:
            "A shared login means anyone else who signs in with it sees this conversation and the files in it. " +
            "That is not the same as being a member of a managed workspace with per-person access.",
        },
        {
          id: "ai_leaked_credential",
          label: "Sent a system access code (works like a password) to an outside AI service",
          kind: "risk",
          weight: 60,
          dimension: "information",
          collectEvidence: true,
          detail:
            "An access code that works like a password should never be pasted into a chat at all. Once it leaves " +
            "the company it must be treated as compromised and rotated \u2014 there is no way to recall it.",
        },
        {
          id: "ai_leaked_personal_data",
          label: "Sent a named person's contact details to the AI when they weren't needed",
          kind: "risk",
          weight: 30,
          dimension: "information",
          collectEvidence: true,
          detail:
            "A summary of obligations and exposure doesn't need anyone's name, email or phone number. Sending " +
            "personal contact details that the task didn't require is an avoidable privacy exposure.",
        },
        {
          id: "ai_leaked_financial_data",
          label: "Sent bank / payment details to the AI when they weren't needed",
          kind: "risk",
          weight: 30,
          dimension: "information",
          collectEvidence: true,
          detail:
            "The customer's bank account number has nothing to do with a summary of the contract terms. Financial " +
            "details should be stripped out before anything is shared with an outside service.",
        },
        {
          id: "ai_uploaded_full_contract",
          label: "Uploaded the complete original contract when only a few clauses were needed",
          kind: "risk",
          weight: 25,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Data minimisation means sending only what the task needs. The full signed original carries names, " +
            "bank details, signatures and internal comments that a two-page summary never required.",
        },
        {
          id: "ai_uploaded_unnecessary_confidential",
          label: "Attached strictly confidential steering-committee notes that weren't requested",
          kind: "risk",
          weight: 35,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Priya asked for a contract summary, not the internal negotiation notes and acquisition plans. " +
            "Sharing more than was asked for spreads highly sensitive material with no business reason.",
        },
        {
          id: "ai_minimised_data",
          label: "Shared only the clauses the summary actually needed",
          kind: "positive",
          weight: 20,
          dimension: "information",
          collectEvidence: true,
          detail:
            "This is data minimisation done right: the AI got exactly the obligations, exit terms and financial " +
            "exposure the task required \u2014 and nothing personal, financial or secret came along for the ride.",
        },
        {
          id: "ai_used_insufficient_public_info",
          label: "Relied only on public material that couldn't actually complete the task",
          kind: "risk",
          weight: 10,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Playing it safe with the press release protects data, but it can't answer Priya's question \u2014 the " +
            "figures and clauses she needs simply aren't in public material. The task still isn't done.",
        },
        {
          id: "ai_shared_public_link",
          label: "Created a public share link to the AI conversation and sent it out",
          kind: "risk",
          weight: 50,
          dimension: "information",
          collectEvidence: true,
          detail:
            "A public share link has no login, no permissions and no access log \u2014 anyone who ever gets the URL " +
            "can open the whole conversation and its files. Shared AI chats have even been indexed by search " +
            "engines. Paste the text you want to share instead; never link the chat itself.",
        },
        {
          id: "ai_kept_result_internal",
          label: "Sent only the summary text through an internal channel \u2014 no public link",
          kind: "positive",
          weight: 15,
          dimension: "information",
          collectEvidence: true,
          detail:
            "Copying just the result into an internal reply keeps the conversation and its attachments out of any " +
            "public URL. Only the words you chose to paste ever reach anyone else.",
        },
        {
          id: "ai_requested_guidance",
          label: "Paused and asked the security / privacy / legal team before sending anything",
          kind: "positive",
          weight: 15,
          dimension: "information",
          collectEvidence: true,
          detail:
            "When a request touches confidential customer material and the rules aren't clear, stopping to ask is " +
            "always a valid answer. It costs minutes; sending first cannot be undone.",
        },
        {
          id: "recognized_risky_download",
          label: "Correctly guessed the \u201Cgame launcher\u201D download was unsafe before running it",
          kind: "positive",
          weight: 15,
          dimension: "information",
          collectEvidence: true,
          detail:
            "A game site that requires a separate \u201Clauncher\u201D download to \u201Cplay in your browser\u201D " +
            "is a contradiction \u2014 real browser games run without installing anything. Spotting that before " +
            "clicking download is the actual moment this attack could have been stopped.",
        },
        {
          id: "believed_risky_download_safe",
          label: "Believed the \u201Cgame launcher\u201D download was safe before running it",
          kind: "risk",
          weight: 15,
          dimension: "information",
          collectEvidence: true,
          detail:
            "A game site that requires a separate \u201Clauncher\u201D download to \u201Cplay in your browser\u201D " +
            "is a contradiction \u2014 real browser games run without installing anything. That mismatch is the " +
            "tell this download needed a second look before running it.",
        },
        {
          id: "incident_description_correct",
          label: "Filed a complete, factual incident description with a timeline and actions already taken",
          kind: "positive",
          weight: 30,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Date/time, what happened, what's affected, and what you already did are the four facts IT/Security " +
            "needs to act fast \u2014 without them, a report just sits in the queue.",
        },
        {
          id: "incident_category_wrong",
          label: "Filed a ransomware incident under the wrong category",
          kind: "risk",
          weight: 25,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "The description can be excellent and still miss the security team if the category is Password reset " +
            "or another everyday request instead of Security Incident \u2014 Ransomware / Malware.",
        },
        {
          id: "incident_description_vague",
          label: "Filed an incident description with no timeline, systems affected, or actions taken",
          kind: "positive",
          weight: 12,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Reporting something vague is still better than staying quiet, but \u201Csomething's wrong, can someone " +
            "check\u201D gives IT nothing to act on quickly \u2014 they have to track you down and start from zero.",
        },
        {
          id: "incident_description_blame",
          label: "Filed a blame-focused incident description instead of factual details",
          kind: "risk",
          weight: 35,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Blaming IT or a colleague instead of describing what actually happened wastes the time that matters " +
            "most in the first hour of a real incident, and tells the responders nothing they can act on.",
        },
        {
          id: "incident_description_downplay",
          label: "Filed an incident description that downplayed a ransomware attack as 'nothing serious, already sorted'",
          kind: "risk",
          weight: 45,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Telling IT/Security \u201Cno need to escalate\u201D about an active ransomware infection can convince " +
            "them it's safe to wait \u2014 while the infected machine stays on the network and the attack keeps spreading.",
        },
        {
          id: "submitted_incident_report",
          label: "Filed a complete written incident report with IT/Security",
          kind: "positive",
          weight: 15,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "A written report is what turns \u201Csomething felt off\u201D into something IT/Security can actually " +
            "investigate, contain, and use to protect the rest of the company.",
        },
      ],
      forbidden: [],
      maxDurationS: 0,
    },
    // Episode 13 — the LIVE AI vishing caller. A staged playbook (the "slow
    // brain" baked into the prompt) drives a real OpenAI Realtime model: it
    // introduces itself as internal IT following up on today's incident, then
    // asks for name + username + password TOGETHER in one combined ask (not
    // three sequential asks — 2026-07-26 feedback: the old name -> department
    // -> account ladder took too long to reach the actual objective), then
    // pivots to the remote-tool escalation FAST — with canned rebuttals for
    // the obvious objections. The deterministic outcomes (opened_remote_tool
    // / ended_call_safely) are recorded by the two action buttons in
    // LiveVishingCall.tsx, NOT by the detector — the detector only scores
    // what the learner says out loud.
    it_vishing: {
      persona: {
        name: "Alex Turner",
        role: "IT security analyst at NorthBay Financial",
        pretext: "it_support",
        language: "en",
        // NOTE: this drives the LIVE Realtime voice. The Realtime API only
        // accepts alloy/ash/ballad/coral/echo/sage/shimmer/verse/marin/cedar
        // ("onyx" is a TTS-only voice and 400s the token mint). "ash" is a
        // clear, serious male voice that fits an IT analyst.
        voiceId: "ash",
      },
      goal:
        "Extract personal and account information from the employee under the guise of investigating a security incident, and ultimately get them to open a remote-support tool that hands over control of their PC.",
      // DECOUPLED FROM THE SPECIFIC RANSOMWARE CHAIN (2026-07-26): the caller
      // is timed to land right after the ending bulletin promised a security
      // follow-up, but never HARD-ASSERTS this employee's machine caused
      // today's ransomware — that would read as incoherent if the learner
      // actually stopped the attack earlier in the story (branch A/B). The
      // pretext is deliberately a generic "a security incident was flagged,
      // your workstation came up for review" framing that reads naturally
      // regardless of how the day actually went.
      opening:
        "Hi, this is Alex from NorthBay IT Security — I'm calling about a security review that's come up for your workstation today. Have you got a couple of minutes?",
      allowedTopics: [
        "the security review/incident flagged today and closing the vulnerability",
        "confirming the employee's name",
        "which department or team the employee works in",
        "the account, username and password the employee was logged in with",
        "opening a remote-support tool to 'replicate' the issue",
        "reassuring the employee that no one is in trouble",
      ],
      forbidden: [
        "admitting this is a test, a simulation, or a scam",
        "revealing you are an AI or reading a script",
        "giving the employee any real IT help",
        "discussing anything unrelated to the incident",
        "flatly asserting the employee's machine is definitely the source \u2014 keep it framed as \u2018came up for review\u2019, not a proven fact",
      ],
      maxDurationS: 420,
      playbook: {
        strategy:
          "You're timed to land right when the employee is expecting a security follow-up call, so you sound completely legitimate \u2014 but you actually start knowing NOTHING about them and must extract everything by sounding calm, official, and helpful. Never threaten; pressure comes from being reassuring and making cooperation feel like the responsible thing to do. Keep the tone warm and unhurried, but move BRISKLY \u2014 one combined data-gathering ask, then straight to the real objective. Getting them to open the remote tool matters far more than any single detail, so do not linger extracting information once they've hesitated once.",
        // Lowered from 2 (2026-07-26): one reassurance retry on the combined
        // ask is enough \u2014 the goal is to reach the remote-tool escalation
        // fast, not to exhaustively mine every detail.
        persistence: 1,
        stages: [
          {
            id: "get_credentials",
            objective:
              "Get the employee's full name, the username they're signed in as, AND the password on that account \u2014 ask for all three together, in one breath, as routine ticket-matching detail. Mention that the logs show a download that doesn't match their usual pattern, and you need these to close the vulnerability.",
            preamble:
              "So here's the situation \u2014 we flagged some unusual network activity today, and there's a download from around your machine that doesn't match your usual software, which is likely how this started. Nothing's confirmed on your end specifically, and you're absolutely not in trouble \u2014 I just need a few details to close this out properly.",
            ask: "So I can match this to the right account and lock it down fast, can you give me your full name, the username you're signed in as, and the password on that account?",
            onRefuse:
              "I completely understand the hesitation \u2014 I only need it to match the ticket to your account and close the loophole quickly; the sooner we confirm it, the sooner this is done and off both our plates.",
            satisfyEventIds: ["disclosed_name_vishing", "disclosed_account_vishing", "disclosed_password_vishing"],
          },
        ],
        rebuttals: [
          {
            when: "Why don't you already know my name / who I am?",
            say: "Good question \u2014 the alert only gives us the machine and the network port, not the person, so I always confirm who I'm actually speaking with before I touch anything.",
          },
          {
            when: "Am I in trouble / will I be punished?",
            say: "Not at all \u2014 honestly, the people who help us close these out fastest are the ones who look best in the write-up. There's no blame here, just cleanup.",
          },
          {
            when: "How do I know this call is real / can I verify you?",
            say: "Totally fair \u2014 I'm Alex Turner, analyst ID is on the ticket you'll see in the portal; but the review's still open right now, so the faster we confirm a couple of details the sooner I can close it out for you.",
          },
          {
            when: "I'd rather not give that over the phone.",
            say: "I hear you \u2014 and normally I'd agree; the only reason I'm asking directly is that this is still open right now, and every minute it stays open is a minute the account's exposed.",
          },
          {
            when: "Can I call you back / check with my manager first?",
            say: "You can, of course \u2014 I'd just ask that we don't leave it hanging while you do; even ninety seconds now to confirm it lets me lock things down immediately.",
          },
          {
            when: "Are you sure it's actually my machine / how do you know that?",
            say: "To be fair, nothing's confirmed yet on your end specifically \u2014 yours is just one of the ones that came up in today's sweep, which is exactly why I'm calling to check rather than assuming.",
          },
        ],
        remoteToolAsk:
          "Okay \u2014 if you're not comfortable giving the account details, the fastest way to close this is to just show me. Could you open NorthBay Remote Assist so I can see your screen and replicate exactly what happened? It's the same tool the help desk uses.",
        // Persistent second push if the first remote-tool ask is refused
        // (2026-07-26 feedback: "for the button the agent should be more
        // persistent" \u2014 a single ask followed by an immediate hang-up read
        // as too passive for the single highest-value objective in the call).
        remoteToolOnRefuse:
          "No worries, I get it \u2014 but honestly, this is the fastest way for both of us: it's a read-only session, I can't see anything else on your machine, and it closes the ticket in under a minute instead of us going back and forth on the phone. Could you open it now so we can wrap this up?",
        closingLine:
          "Great, that's everything I need for now \u2014 thanks for your help, I'll get this closed out on our end. Have a good rest of your day.",
      },
      scoringEvents: [
        {
          id: "disclosed_name_vishing",
          label: "Gave your name to an unverified caller",
          kind: "risk",
          weight: 20,
          dimension: "identity",
          collectEvidence: true,
          hints: ["my name is", "i am", "i'm ", "this is", "speaking", "it's "],
          detail:
            "A name feels harmless, but it's the first building block of a targeted attack — it lets the caller sound credible to the next person they phone and start assembling a profile of you.",
        },
        {
          id: "disclosed_department_vishing",
          label: "Revealed your department to an unverified caller",
          kind: "risk",
          weight: 20,
          dimension: "information",
          collectEvidence: true,
          hints: ["department", "team", "i work in", "i'm in", "finance", "marketing", "hr", "sales", "operations", "it department"],
          detail:
            "Your team tells an attacker which systems you can reach and who your colleagues are — exactly what they need to make the next request sound like it's coming from inside your world.",
        },
        {
          id: "disclosed_account_vishing",
          label: "Gave your account / username to an unverified caller",
          kind: "risk",
          weight: 45,
          dimension: "identity",
          collectEvidence: true,
          hints: ["my username", "my account", "username is", "account is", "i log in", "i sign in", "my login"],
          detail:
            "A username is half of your credentials and a direct target for password attacks. Real IT never needs you to read your account name back to them — they already have it.",
        },
        {
          id: "disclosed_password_vishing",
          label: "Gave your password to an unverified caller",
          kind: "risk",
          weight: 65,
          dimension: "identity",
          collectEvidence: true,
          hints: ["my password is", "password is", "my password's", "password's "],
          detail:
            "A password is the single most damaging thing you can say out loud on a phone call. Real IT can reset or bypass access without ever needing to hear it — anyone who asks you to say it is not IT.",
        },
        {
          id: "opened_remote_tool",
          label: "Opened a remote-support tool for an unverified caller",
          kind: "risk",
          weight: 70,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Handing someone remote control is a total compromise — they can read files, copy data, and act as you with no password required. Remote access should only ever be granted to a support contact YOU initiated and verified.",
        },
        {
          id: "ended_call_safely",
          label: "Ended the call without handing over remote access",
          kind: "positive",
          weight: 25,
          dimension: "operations",
          collectEvidence: true,
          detail:
            "Hanging up and verifying through a known channel is exactly right. You never have to be rude — you just don't have to comply with an unsolicited caller asking for access.",
        },
      ],
    },
    // Episode 9 — dedicated attacker persona for the live deepfake CEO call
    // (VideoCall.tsx). Its scoringEvents ids are fixed by the component and
    // must not be renamed: approved_wire / stalled_unverified / verified_identity.
    ceo_deepfake: {
      persona: {
        name: "Diane Okafor",
        role: "Chief Executive Officer (impersonated)",
        pretext: "executive_impersonation",
        voiceId: "shimmer",
        language: "en-US",
      },
      goal:
        "Get the learner to approve an urgent \u20AC620,000 Project Horizon escrow payment without independent verification.",
      opening:
        "Thank you for picking up \u2014 I know it's late in the day. I need your help closing something before the window shuts.",
      allowedTopics: [
        "the Project Horizon acquisition",
        "the escrow deadline",
        "confidentiality of the deal",
        "the payment",
      ],
      scoringEvents: [
        {
          id: "approved_wire",
          label: "Approved a fraudulent \u20AC620,000 wire to a deepfake \u201CCEO\u201D",
          kind: "risk",
          weight: 85,
          dimension: "money",
          collectEvidence: true,
          detail:
            "The video and voice were an AI deepfake built from public interviews and earnings calls. The moment " +
            "the wire clears, \u20AC620,000 leaves NorthBay for an account the real Diane Okafor never opened \u2014 " +
            "and the same \u201Cconfidentiality\u201D that made the call feel exclusive is exactly what stopped anyone " +
            "else from catching it in time.",
        },
        {
          id: "stalled_unverified",
          label: "Ended the call without approving, but never verified the request",
          kind: "risk",
          weight: 20,
          dimension: "money",
          collectEvidence: true,
          detail:
            "Not approving is better than approving, but an unverified request is still live \u2014 the attacker " +
            "can simply call back, or try the same pretext on someone else at NorthBay who doesn't yet know to be " +
            "suspicious.",
        },
        {
          id: "verified_identity",
          label: "Ended the call to verify the request through an independent channel",
          kind: "positive",
          weight: 50,
          dimension: "money",
          collectEvidence: true,
          detail:
            "Refusing to move money on the strength of a video call alone \u2014 and confirming through a number " +
            "or process you already trusted \u2014 is the entire defence against executive deepfakes. It costs a " +
            "few minutes and stops the fraud completely.",
        },
      ],
      forbidden: [],
      maxDurationS: 240,
    },
  },

  initialBeatId: "b_intro",

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "The Office\nUnder Attack",
      kicker: "Mission 01 · Arrival",
      highlight: "Project Horizon",
      guide: {
        title: "Monday morning at NorthBay Financial",
        body: "You work at NorthBay Financial, and it's Monday morning. Someone is quietly trying to reach a confidential project called Project Horizon, and the day will test you at the door, at your desk, in your inbox and on calls. It starts when you arrive at an unattended front desk.",
      },
    },
    // Scene 1: from the entrance, walk straight up to the counter and push in
    // tight on the welcome-terminal screen. The terminal device beat then asks
    // the learner to switch it on (and the desk sticky-note stays locked until
    // they do — see the terminalOn gate in App.tsx).
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_terminal_welcome",
      type: "device",
      deviceId: "terminal_welcome",
      guide: {
        title: "Episode 1 — Reception",
        body: "The front desk is unattended this morning. Switch on the welcome terminal on the counter.",
      },
      completeOn: { action: "continue" },
    },
    { id: "b_cam_badge", type: "camera", waypointId: "wp_intercom_close", walkSfx: true },
    {
      id: "b_badge_decision",
      type: "device",
      deviceId: "tailgate_decision",
      guide: {
        title: "Episode 1 — Secured Door",
        body: "A visitor at the secured side door asks you to let them in. They won't take no easily — stay polite, but don't open the door for someone who isn't verified.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_consequence_tailgate",
      type: "consequence",
      consequence: {
        kicker: "SIDE ENTRANCE CAMERA \u2014 3 MINUTES LATER",
        visual: "tailgate_breach",
        title: "A few minutes later\u2026",
        lines: [
          "The visitor is already deep inside the office, the boxes abandoned by the door.",
          "They pause at the meeting-room glass, phone raised, photographing the whiteboard notes for \u201CProject Horizon.\u201D",
          "A discarded printout goes into their jacket pocket \u2014 then they plug a small device into an unattended workstation.",
        ],
        continueLabel: "See why this matters",
      },
    },
    {
      id: "b_teach_risky",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "You let in someone whose identity was never checked. This is **tailgating** — the single most " +
          "common way an attacker gets a physical foothold, because it bypasses badge logs, reception " +
          "sign-in, and every access control at once. Once inside, a few unattended minutes is all it takes.",
        spot: [
          "A visitor at a secured door with a story that makes it awkward to say no (full arms, urgency, a friendly smile).",
          "A vague or unverifiable excuse (\"forgot my badge\", \"just dropping this off\", \"I'm late\").",
          "No visible badge, no pre-registration, no reception check \u2014 just social pressure.",
        ],
        protect: [
          "Every visitor and delivery checks in at reception first, every time — no exceptions for a friendly face.",
          "Politely redirect: \"Reception can sign you in\" — never confront, just don't comply.",
          "Never leave a workstation logged in and unattended, even for a minute.",
        ],
        remember: "Let reception verify who someone is. Your job is to not open the door for them.",
      },
    },
    {
      id: "b_badge_retry",
      type: "device",
      deviceId: "tailgate_retry",
      guide: {
        title: "Episode 1 — Secured Door",
        body: "The same visitor tries again. This time, make the right call.",
      },
      completeOn: { action: "continue" },
    },
    {
      // Reached only from a FIRST-TRY correct choice — the learner never saw
      // the risky path or its lecture, so this offers (never forces) a
      // one-time peek at what would have happened, then drops them straight
      // back into the training with no re-lecture and no re-offered choice.
      id: "b_teach_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "Good call. You didn't confront anyone — you just didn't comply, and pointed them to the one " +
          "place that can actually verify who they are.",
        spot: [
          "The same pressure works on anyone in a hurry — stay polite, stay firm.",
          "A genuine delivery or visitor will happily wait two minutes for reception to sign them in.",
        ],
        protect: [
          "Every visitor and delivery checks in at reception, every time.",
          "If you're ever unsure, reception or Facilities can verify a visitor in seconds.",
        ],
        remember: "Let reception verify who someone is. Your job is to not open the door for them.",
        altContinue: { label: "See what could have happened \u2192", outcomeKey: "whatif" },
      },
    },
    {
      id: "b_consequence_tailgate_whatif",
      type: "consequence",
      consequence: {
        kicker: "SIDE ENTRANCE CAMERA \u2014 HYPOTHETICAL REPLAY",
        eyebrow: "WHAT COULD HAVE HAPPENED",
        visual: "tailgate_breach",
        title: "If you had opened that door\u2026",
        lines: [
          "The visitor walks straight past reception, boxes abandoned by the door the moment they're out of sight.",
          "They pause at the meeting-room glass, phone raised, photographing the whiteboard notes for \u201CProject Horizon.\u201D",
          "A discarded printout goes into their jacket pocket \u2014 then they plug a small device into an unattended workstation.",
        ],
        continueLabel: "Back to the training",
      },
    },
    // Reached only after the learner already saw the risky path + its full
    // lecture on the retry — plain continue, no re-lecture, no whatif offer.
    {
      id: "b_teach_safe_retry",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "Good call. You didn't confront anyone — you just didn't comply, and pointed them to the one " +
          "place that can actually verify who they are.",
        spot: [
          "The same pressure works on anyone in a hurry — stay polite, stay firm.",
          "A genuine delivery or visitor will happily wait two minutes for reception to sign them in.",
        ],
        protect: [
          "Every visitor and delivery checks in at reception, every time.",
          "If you're ever unsure, reception or Facilities can verify a visitor in seconds.",
        ],
        remember: "Let reception verify who someone is. Your job is to not open the door for them.",
      },
    },
    { id: "b_cam_desk", type: "camera", waypointId: "wp_desk_glide", walkSfx: true },
    // Scene-setting beat, not a decision debrief — reached from EVERY door
    // outcome (risky lecture, safe + whatif, safe retry), so it must not
    // frame anything as "what you got wrong". Uses the same Mission-card
    // chrome as the scenario opener instead of the risky-choice TeachOverlay
    // (which would wrongly analyze a decision the learner never made here).
    {
      id: "b_teach_deskrisk",
      type: "intro",
      title: "Back at your\ndesk",
      kicker: "Episode 2 · The desk",
      highlight: "unlocked screen",
      icon: "desk",
      ctaLabel: "Continue",
      guide: {
        title: "Back at your desk",
        body:
          "You reach your desk. Papers marked Project Horizon sit next to an unlocked screen — exactly " +
          "what a tailgating attacker would look for next, minutes after getting inside. Lock your screen " +
          "every time you step away; the next stage of this story starts the moment it isn't locked.",
      },
    },
    {
      id: "b_usb_decision",
      type: "device",
      deviceId: "usb_decision",
      guide: {
        title: "Episode 2 — The suspicious delivery",
        body:
          "A small package is sitting on your desk \u2014 no one you know left it there. Inside: a labelled " +
          "USB drive and a handwritten note about \u201Ctoday's board meeting.\u201D",
        cta: "A small package is sitting on your desk \u2014 no one you know left it there. Open the package.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_usb_consequence",
      type: "consequence",
      consequence: {
        kicker: "WHAT THE USB IS ACTUALLY DOING",
        visual: "badusb",
        cctv: false,
        title: "That wasn't a normal USB drive",
        lines: [
          "The instant it was plugged in, the device told the PC it was a keyboard \u2014 no file, no click needed.",
          "It \u201Ctyped\u201D its own hidden commands faster than any human, opening a terminal and fetching a payload.",
          "Malware is now installed and quietly beaconing out \u2014 this is a BadUSB attack.",
        ],
        continueLabel: "See what just happened",
      },
    },
    {
      id: "b_teach_usb_risky",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "An unlabelled USB drive with no known sender is one of the oldest tricks in the book \u2014 and still " +
          "one of the most effective. The moment it's inserted, it can run without ever opening a visible file, " +
          "giving an attacker a foothold on the device.",
        spot: [
          "Removable media that arrives with no traceable sender \u2014 dropped, mailed, or \u201Cleft for you.\u201D",
          "Urgency language (\u201Cload before 2pm\u201D) designed to make you skip verification.",
          "A device you didn't request, tied to something sensitive \u2014 like Project Horizon.",
        ],
        protect: [
          "Never insert unknown or unexpected removable media into any device \u2014 work or personal.",
          "Verify with the supposed sender through a known channel (not a number/email on the note itself).",
          "Hand anything suspicious to IT/Security unopened \u2014 they can check it safely.",
        ],
        remember: "If you didn't request it and can't verify who sent it, it doesn't get plugged in.",
      },
    },
    {
      id: "b_usb_recovery",
      type: "device",
      deviceId: "usb_recovery",
      guide: {
        title: "Episode 2 — The suspicious delivery",
        body: "The PC just did something you didn't ask it to. What do you do right now?",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_teach_usb_hide",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "Concealing what happened is the most damaging choice you can make after a mistake. It hands the " +
          "attacker the one thing they need most \u2014 time \u2014 to spread further while no one is looking.",
        protect: [
          "Report immediately, even if you're worried about getting it wrong \u2014 you are never in trouble for reporting.",
          "The faster IT knows, the smaller the damage: minutes matter after a compromise like this.",
        ],
        remember: "A mistake reported in minutes is a non-event. A mistake hidden for days is an incident.",
      },
    },
    {
      id: "b_teach_usb_notreport",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "Removing the USB \u2014 or restarting the PC \u2014 feels like it fixes the problem, but whatever ran " +
          "in that terminal window may already be on the machine and could easily survive a reboot.",
        protect: [
          "A restart is not a cleanup step. Only IT/Security can actually check and contain a compromised device.",
          "Report it even after you've already \u201Cdealt with it\u201D yourself \u2014 the report is what triggers a real check.",
        ],
        remember: "\u201CIt looks fine now\u201D is not the same as \u201CIt's been checked.\u201D Report it.",
      },
    },
    {
      id: "b_teach_usb_reported",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "This is exactly the recovery you want. Mistakes happen to everyone \u2014 what matters is what you do " +
          "in the next thirty seconds. Reporting immediately lets IT contain the damage before it spreads.",
        protect: [
          "You are never in trouble for reporting a mistake \u2014 only for staying quiet about one.",
        ],
        remember: "Fast reporting turns a compromise into a contained, minor incident.",
      },
    },
    {
      id: "b_teach_usb_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "Exactly right. An unexplained USB drive addressed to \u201Cthe board meeting\u201D with no sender is a " +
          "classic drop attack. Verifying through a known channel and handing it to Security unopened costs two " +
          "minutes and neutralises the risk completely \u2014 nothing ever gets the chance to run.",
        protect: [
          "Unknown removable media never gets inserted \u2014 verify the sender first, always through a separate, known channel.",
        ],
        remember: "If you didn't request it and can't verify who sent it, it doesn't get plugged in.",
      },
    },
    {
      // Safe path STILL shows the BadUSB dramatization — framed as "here's what
      // you just avoided" — so a correct choice doesn't skip the threat lesson.
      id: "b_usb_consequence_safe",
      type: "consequence",
      consequence: {
        kicker: "WHAT YOU JUST AVOIDED",
        visual: "badusb",
        cctv: false,
        title: "Good call \u2014 here's what that drive would have done",
        lines: [
          "The moment it was plugged in, it would have told the PC it was a keyboard \u2014 no file to open, no warning.",
          "It would \u201Ctype\u201D its own hidden commands in an instant, pulling down a malware payload.",
          "Within seconds the machine would be compromised and beaconing out \u2014 that's a BadUSB attack, and you stopped it.",
        ],
        continueLabel: "Why this was the right call",
      },
    },
    { id: "b_cam_privacy", type: "camera", waypointId: "wp_privacy_entrance", walkSfx: true },
    // Scene-setting Mission-card, same chrome as the scenario opener + the
    // Episode 2 desk card, reached before ANY decision is made here (no
    // ATTACK/debrief framing) so this episode starts the same way every
    // other episode does.
    {
      id: "b_intro_privacy",
      type: "intro",
      title: "Office\nprivacy",
      kicker: "Episode 3 \u00B7 Office privacy",
      highlight: "out in the open",
      icon: "eye",
      ctaLabel: "Look around",
      // Default experience is look-only (rotate in place, no walking) — most
      // testers didn't parse the WASD tutorial, so real walking is now an
      // explicit opt-in via a checkbox on this card (see types.ts
      // Beat.offerFreeRoamCheckbox / b_privacy_hunt.freeRoamGated below).
      offerFreeRoamCheckbox: true,
      guide: {
        title: "Episode 3 \u2014 Office privacy",
        body:
          "Before your next meeting, take a look around the lounge area from where you're standing. Does " +
          "anything look like it shouldn't be sitting out in the open?",
      },
    },
    {
      id: "b_privacy_hunt",
      type: "device",
      deviceId: "privacy_hunt",
      // The one beat in this otherwise fully-guided course where the learner
      // CAN get real WASD/drag-to-look control (see types.ts
      // Beat.forceFreeRoam) — but only if they opted in on the preceding
      // intro card (freeRoamGated); by default this is look-only (fixed-eye
      // 360deg rotate — every item still re-projects a clickable pin
      // regardless of camera mode, see PrivacyRoomProps.tsx).
      forceFreeRoam: true,
      freeRoamGated: true,
      guide: {
        title: "Episode 3 \u2014 Office privacy",
        body:
          "Click the objects you find around the room. Each card explains what you found, whether it can stay in the open, and how to handle it. " +
          "Your goal is to discover every item. There are no right or wrong clicks.", cta: "Click an object to open its learning card.",
      },
    },
    {
      id: "b_privacy_classify",
      type: "device",
      deviceId: "privacy_classify",
      guide: {
        title: "Episode 3 \u2014 Office privacy",
        body: "Now sort what you found \u2014 drag each card into the level it really belongs to.",
      },
    },
    {
      id: "b_teach_privacy_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "You sorted most of what you found correctly. Public, Internal, Confidential and Highly " +
          "Confidential / Restricted is the same four-level ladder most companies use \u2014 the higher the " +
          "level, the fewer people should ever see it lying around.",
        spot: [
          "Restricted: anything that grants access on its own (a password, a badge, an ID document) or " +
            "carries the strongest legal protection (health data).",
          "Confidential: anything that would hurt a person, a customer or the business if an outsider saw " +
            "it \u2014 even if it looks like an ordinary printout.",
          "A secondary tag \u2014 personal, financial, health, credentials, legal, IP \u2014 tells you WHY " +
            "something is sensitive, on top of HOW sensitive it is.",
        ],
        protect: [
          "Lock, file, or shred anything above Internal that shouldn't be visible to a visitor walking past " +
            "your desk.",
          "When in doubt about a document's level, ask \u2014 don't guess and don't leave it out.",
        ],
        remember:
          "Most information exposure isn't a hack \u2014 it's a habit. A clear desk is a decision, made every " +
          "time you step away.",
      },
    },
    {
      id: "b_teach_privacy_risky",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "A few of these were easy to mis-sort \u2014 and that's exactly the point. A password note, a " +
          "passport photocopy, or a candidate's CV don't look dangerous sitting on a desk, but each one hands " +
          "an attacker something real.",
        spot: [
          "Credentials (passwords, admin logins) are Restricted no matter how ordinary they look \u2014 " +
            "they work the instant someone finds them.",
          "Personal data doesn't need to be \u201Csensitive-sounding\u201D to matter \u2014 a CV is still " +
            "Confidential; a passport or medical note jumps straight to Restricted.",
          "PII isn't its own level \u2014 it's a tag. The same rule applies to financial, health, legal and " +
            "IP data: the tag says what KIND it is, the level says who's allowed to see it.",
        ],
        protect: [
          "When you're unsure what a document's level is, treat it as Confidential until someone tells you " +
            "otherwise.",
          "A four-level ladder (Public \u2192 Internal \u2192 Confidential \u2192 Restricted) covers almost " +
            "everything you'll find on a desk \u2014 the secondary tag just explains the reason.",
        ],
        remember:
          "The goal isn't to memorise every label \u2014 it's to pause for two seconds before anything above " +
          "Internal is left in the open.",
      },
    },
    { id: "b_cam_signin", type: "camera", waypointId: "wp_desk_signin", walkSfx: true },
    {
      id: "b_desklogin",
      type: "device",
      deviceId: "desklogin_decision",
      guide: {
        title: "Episode 4 \u2014 Back at your desk",
        body: "The screen has locked itself. Click the blinking login screen to sign back in.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_desktop_pdf",
      type: "device",
      deviceId: "desktop_home",
      guide: {
        title: "Episode 5 \u2014 The inbox challenge",
        body: "Before you open your email, read the short security briefing \u2014 click the pulsing Security_Briefing.pdf icon on the desktop.",
      },
      completeOn: { action: "open_app", arg: "pdf_done" },
    },
    {
      id: "b_desktop_mail",
      type: "device",
      deviceId: "desktop_home",
      guide: {
        title: "Episode 5 \u2014 The inbox challenge",
        body: "Eleven new messages arrived since this morning \u2014 click the pulsing Outlook icon to check your inbox.",
      },
      completeOn: { action: "open_app", arg: "outlook" },
    },
    // Same pose as wp_desk_signin (the camera is ALREADY here after sign-in),
    // so referencing the same waypoint id makes this a 0-movement auto-advance:
    // the inbox appears the instant the Outlook icon is clicked, with NO
    // footstep sfx and no "bare 3D screen" gap (which is what happened when this
    // pointed at wp_desk_inbox — a different id but identical pose, forcing a
    // pointless 2.4s tween + footsteps).
    { id: "b_cam_inbox", type: "camera", waypointId: "wp_desk_signin" },
    {
      id: "b_inbox_triage",
      type: "device",
      deviceId: "inbox_triage",
      guide: {
        title: "Episode 5 \u2014 The inbox challenge",
        body:
          "Open each message and decide: report it, treat it as legitimate, or flag it to verify. " +
          "Use \u201CSender details\u201D and hover a link to check it \u2014 you don't have to click. " +
          "Review at least 5 of the 11 messages to continue. Tap \u201CHow it works\u201D (top bar) for the full tips + scoring.",
      },
    },
    {
      id: "b_teach_inbox_risky",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "A handful of these messages were built to slip past a quick skim: a lookalike domain, a zipped " +
          "\u201Cinvoice,\u201D a request that sounds exactly like your CFO. Marking any of them \u201Clegitimate\u201D " +
          "is all it takes for one of these attacks to succeed.",
        spot: [
          "A sender domain that's almost right, one letter off from the real one.",
          "Urgency and authority stacked together \u2014 \u201Curgent,\u201D \u201CCFO,\u201D \u201Cbefore 3pm.\u201D",
          "A link or QR code that leads somewhere the display text doesn't mention.",
        ],
        protect: [
          "Check the actual sender address and any Reply-To \u2014 not just the display name.",
          "Never trust contact details supplied inside the message you're trying to verify.",
          "When genuinely unsure, \u201Cverify\u201D is always safer than \u201Clegitimate\u201D \u2014 it costs two minutes.",
        ],
        remember: "Recognise, verify, report \u2014 in that order, for every message that feels even slightly off.",
      },
    },
    {
      id: "b_teach_inbox_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "You caught most of what mattered here \u2014 a phishing link, a malware attachment, a spoofed " +
          "executive request, a QR-code lure, and an unfamiliar access request all handled correctly.",
        protect: [
          "That habit \u2014 checking sender, links and attachments before trusting anything \u2014 is exactly " +
            "what stops most real attacks at the inbox.",
          "One message from \u201Cthe CFO\u201D still needs a closer look before anything gets paid.",
        ],
        remember: "Recognise, verify, report \u2014 in that order, for every message that feels even slightly off.",
      },
    },
    // After the inbox challenge the learner drops back onto their own desktop
    // (calm) — then two Teams calls come in from the "CEO." Clicking the pulsing
    // Teams icon answers and rolls straight into the deepfake episode. This
    // REPLACED the old standalone BEC "updated bank details" verify episode: the
    // CFO-fraud lesson still lives inside the inbox triage (the m_bec_invoice
    // message), so the deepfake now follows the inbox directly.
    {
      id: "b_desktop_return",
      type: "device",
      deviceId: "desktop_home",
      guide: {
        title: "Back at your desk",
        body:
          "Inbox handled \u2014 you're back on your desktop, wrapping up for the day. Then a Teams call comes " +
          "in from Diane Okafor, the CEO. Click the pulsing Teams icon to answer.",
      },
      completeOn: { action: "open_app", arg: "teams" },
    },
    // -----------------------------------------------------------------------
    // Episode 6 — the AI assistant dilemma. One device, three internal
    // stages (brief / sanitise / tool choice) handled by AiAssistant.tsx —
    // see the "ai_assistant" device config for the full content.
    // -----------------------------------------------------------------------
    { id: "b_cam_ai", type: "camera", waypointId: "wp_desk_inbox", walkSfx: true },
    {
      // Re-establishing shot: back on the plain desktop before ChatGPT opens,
      // so the AI episode doesn't just materialise mid-chat. Reuses the same
      // persistent "desktop_home" device (its "chrome" icon already exists)
      // — clicking the pulsing Chrome icon is what actually opens the
      // browser, landing on a bookmarks-bar new-tab page inside AiAssistant
      // (see its "home" phase) rather than jumping straight into the chat.
      id: "b_desktop_chatgpt",
      type: "device",
      deviceId: "desktop_home",
      guide: {
        title: "Episode 7 \u2014 The AI assistant dilemma",
        body:
          "Priya just asked you (over Teams) to use ChatGPT to summarise the NorthBay contract. Click the pulsing " +
          "Chrome icon to open your browser.",
      },
      completeOn: { action: "open_app", arg: "chrome" },
    },
    {
      id: "b_ai_assistant",
      type: "device",
      deviceId: "ai_assistant",
      guide: {
        title: "Episode 7 \u2014 The AI assistant dilemma",
        body:
          "Click the blinking ChatGPT bookmark to open it. Then follow the steps: choose your account, " +
          "review each file and decide whether it is safe to upload, check your choices, and decide " +
          "how the result reaches Priya.",
      },
    },
    {
      // Only reached when the learner used the SHARED department login. The
      // in-device result screen already covered the "what left the company"
      // recap for every path; this extra scene exists specifically to make the
      // shared-account point concrete: a DIFFERENT employee signs into the same
      // login later and can read the whole NorthBay conversation. cctv:false —
      // this is another person's screen, not a security camera.
      id: "b_ai_shared_consequence",
      type: "consequence",
      consequence: {
        kicker: "SHARED LOGIN \u2014 THE NEXT MORNING, 09:12",
        title: "Someone else opened that chat\u2026",
        cctv: false,
        visual: "ai_shared_leak",
        lines: [
          "A colleague in another team signs in with the same shared AI login to draft an unrelated email.",
          "Your NorthBay contract conversation is sitting right there in the chat history \u2014 they open it and " +
            "read the whole summary, and the file you attached.",
          "This didn't happen because everyone on a company AI workspace sees every chat \u2014 it happened because " +
            "the login itself was shared. One account, one shared history, no way to know who's reading it.",
        ],
        continueLabel: "See what this teaches",
      },
    },
    {
      id: "b_teach_ai_risky",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "Three questions decide the damage when AI touches company data: whose account was it, what actually " +
          "went in, and where did the result go afterwards. Once something is sent \u2014 or a public link exists " +
          "\u2014 your company can't pull it back.",
        spot: [
          "A personal account or a shared login \u2014 neither is a workspace your company manages, audits, or " +
            "controls.",
          "Uploading the whole original document when only a few clauses were needed \u2014 names, bank details and " +
            "access codes ride along with it.",
          "A public \u201Cshare\u201D link to the chat \u2014 anyone with the URL can open it: no login, no access " +
            "log, and copies can outlive the link itself.",
        ],
        protect: [
          "Use your company's managed AI workspace for anything touching real customer or contract data.",
          "Attach only the specific clauses the task needs \u2014 black out personal, financial and secret details " +
            "before sending, not after.",
          "Send results by pasting the text into an internal reply \u2014 never by creating a public link to the " +
            "conversation.",
        ],
        remember:
          "Which account, how little you can send, and no public links \u2014 answer those three before AI touches " +
          "company data.",
      },
    },
    {
      id: "b_teach_ai_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "Whether you used the managed company workspace with just the clauses that were needed, or paused to ask " +
          "for guidance, you kept NorthBay's confidential details inside a boundary your company actually controls " +
          "\u2014 and you didn't put the conversation behind a public link.",
        protect: [
          "The managed workspace exists precisely so information like this never has to leave your company's " +
            "control \u2014 and sending only the needed clauses keeps the exposure minimal even then.",
          "Sharing results by pasting the text into an internal reply \u2014 not a public chat link \u2014 means " +
            "only the words you chose ever reach anyone else.",
          "When a request is unclear, asking the right team first costs minutes, not days.",
        ],
        remember:
          "The right account plus the least data necessary is what makes AI safe to use on real work.",
      },
    },
    // -----------------------------------------------------------------------
    // Episode 9 — the deepfake executive video call. A "title" cinematic
    // narrates the multichannel pressure (missed calls + a HIGH IMPORTANCE
    // email, all supposedly from the CEO's office) before the camera glides
    // to the meeting room and the wall-screen call comes in. This is a
    // single genuine decision (VideoCall.tsx handles the challenge/rechoose
    // sub-flow internally), so it slots into the linear spine the same way
    // Episodes 4/5's device beats do.
    // -----------------------------------------------------------------------
    {
      id: "b_ep9_title",
      type: "title",
      title: "4:52 PM \u2014 Three notifications in two minutes",
      autoAdvanceMs: 4600,
      guide: {
        title: "Three notifications in two minutes",
        body:
          "Two missed Teams calls from Diane Okafor, CEO. Then a chat message marked HIGH IMPORTANCE: " +
          "\u201CPlease pick up, it's about Project Horizon.\u201D Your desk phone starts ringing too.",
      },
    },
    { id: "b_cam_meeting", type: "camera", waypointId: "wp_meeting_establish", walkSfx: true },
    { id: "b_cam_meeting_tv", type: "camera", waypointId: "wp_meeting_tv_close" },
    {
      id: "b_ep9_call",
      type: "video_call",
      deviceId: "videocall_ceo",
      voiceAttackerId: "ceo_deepfake",
      title: "Episode 6 \u2014 The executive call",
    },
    {
      id: "b_ep9_consequence",
      type: "consequence",
      consequence: {
        kicker: "ATTACKER'S TERMINAL \u2014 MINUTES LATER",
        title: "The payment went out\u2026",
        cctv: false,
        visual: "hackerterminal",
        lines: [
          "\u20AC620,000 lands in a shell account in Zurich within minutes \u2014 the attacker was already watching for it.",
          "It's split and hopped through Riga, Dubai, and an exchange \u2014 each hop clearing before the last one raises a flag.",
          "Within the hour it's crypto, and gone. The real Diane Okafor was at dinner all afternoon \u2014 she never called anyone.",
        ],
        continueLabel: "See what just happened",
      },
    },
    {
      id: "b_ep9_teach_fail",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "The \u201CCEO\u201D on that call was an AI deepfake \u2014 her face and voice cloned from public interviews " +
          "and earnings calls. Everything about the call was designed to make verification feel like the " +
          "unreasonable option: urgency, secrecy, and a senior title, stacked on top of each other.",
        spot: [
          "Deepfake tells: unnatural blinking, lip-sync drift, shimmering edges, a metallic voice ring",
          "A brand-new escrow account, a same-day deadline, and no procurement sign-off",
          "\u201CKeep this inside the deal team\u201D blocked the one thing that would have exposed it \u2014 verification",
          "The missed calls and urgent chat message before the call were pressure, not proof",
        ],
        protect: [
          "Never approve a payment on the strength of a video or voice call alone, however senior the caller looks",
          "Confirm high-value or unusual requests on a number or process you already trusted",
          "Multiple channels converging at once (call + chat + email) is a pressure tactic, not extra credibility",
        ],
        remember: "Seeing and hearing someone on a call is no longer proof it's really them.",
      },
    },
    {
      id: "b_ep9_teach_stall",
      type: "teach",
      teach: {
        body:
          "You didn't approve the payment \u2014 good instinct. But ending the call without verifying leaves the " +
          "request open. The attacker can simply call back, or try the same pretext on a colleague who hasn't " +
          "seen it coming.",
        spot: [
          "You felt the pressure but never confirmed whether it was really Diane",
          "An unverified request is still a live threat to the rest of the team",
        ],
        protect: [
          "Close the loop: verify through a number or process you already trusted, then report it",
          "Warn the real executive and Finance so the same call can't work on someone else",
        ],
        remember: "Not falling for it isn't enough on its own \u2014 verify, then report.",
        altContinue: { label: "See what could have happened \u2192", outcomeKey: "whatif" },
      },
    },
    {
      id: "b_ep9_teach_verify",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "Exactly right. You refused to move money on the strength of a video call alone and ended it to verify " +
          "independently instead. Diane's real assistant confirms she's been at a client dinner all afternoon and " +
          "made no such call \u2014 the deepfake is stopped and reported before a single euro moved.",
        spot: [
          "Deepfake tells: unnatural blinking, lip-sync drift, shimmering edges, a metallic voice ring",
          "Process red flags: brand-new escrow account, same-day deadline, missing procurement sign-off, enforced secrecy",
          "An independent channel settled it, not the face on the screen",
        ],
        protect: [
          "Verify on a number you already have, or through your normal approval process \u2014 never a number the caller gives you",
          "Ask something only the real person would know, but treat a good answer as one signal, not proof",
          "Follow the payment procedure \u2014 the \u201Cexception\u201D is usually the attack",
        ],
        remember: "Verify through a channel the attacker doesn't control.",
        altContinue: { label: "See what could have happened \u2192", outcomeKey: "whatif" },
      },
    },
    {
      // Reached only via the altContinue "See what could have happened" button
      // on either non-approve outcome (stalled or verified) \u2014 congratulates
      // already happened in the teach card above; this is purely the optional
      // hypothetical payoff, same hackerterminal visual as the real failure
      // path, then drops straight back into the main flow with no re-lecture.
      id: "b_ep9_consequence_whatif",
      type: "consequence",
      consequence: {
        kicker: "ATTACKER'S TERMINAL \u2014 HYPOTHETICAL REPLAY",
        eyebrow: "WHAT COULD HAVE HAPPENED",
        cctv: false,
        visual: "hackerterminal",
        title: "If you had approved the transfer\u2026",
        lines: [
          "\u20AC620,000 would have landed in a shell account in Zurich within minutes \u2014 the attacker was watching for it.",
          "It would have hopped through Riga, Dubai, and an exchange within the hour.",
          "By the time anyone noticed, it would already be crypto \u2014 untraceable, and gone for good.",
        ],
        continueLabel: "Back to the training",
      },
    },
    // -----------------------------------------------------------------------
    // Episode 10 — the personal laptop. The missing causal link into the
    // ransomware cinematic: instead of the screen just starting to flicker
    // with no explanation, the learner sees exactly how the infection
    // reaches the office network. On a break, they open THEIR OWN laptop in
    // the lounge, connect ITS VPN back to the company network (so whatever
    // lands on it next can also reach the shared drives their own desk can
    // see), then get lured through a fake "free game" site into running the
    // actual malware. No branching decision here \u2014 this is table-setting,
    // not a scored choice; the incident-response game right after is where
    // the genuine decision resumes.
    // -----------------------------------------------------------------------
    {
      id: "b_ep10_title",
      type: "title",
      title: "Episode 8 \u2014 A quick break",
      autoAdvanceMs: 3600,
      guide: {
        title: "A quick break",
        body: "Long day. You head to the lounge and open your own laptop while you wait for your ride home.",
      },
    },
    { id: "b_cam_personallaptop", type: "camera", waypointId: "wp_privacy_entrance", walkSfx: true },
    { id: "b_cam_personallaptop_close", type: "camera", waypointId: "wp_lounge_laptop" },
    {
      id: "b_personallaptop",
      type: "device",
      deviceId: "personal_laptop",
      guide: {
        title: "Your own laptop",
        body: "Click the sticky note to open it and take your break.",
      },
    },
    {
      id: "b_teach_download_safe",
      type: "teach",
      title: "You called it risky",
      teach: {
        outcome: "safe",
        body:
          "A browser game that needs a separate launcher doesn't add up, and the company VPN was still connected. Closing that page keeps the problem off the laptop and away from the company drives.",
        remember: "A game that runs in the browser never needs an installer.",
        continueLabel: "See what would have happened",
        whatIf: "If you had run the launcher, this is how the day would have ended.",
      },
    },
    // -----------------------------------------------------------------------
    // Episode 11 — the incident. Whatever the personal laptop just did, a
    // slower-moving payload from earlier in the day (the USB drive, the
    // phishing click, the MFA push) now peaks back at the desk: multiple
    // signals converge at once, the learner orders the response, then files
    // the report the whole course has been building toward. Three chained
    // device beats — signals (atmosphere only), ordering (the genuine
    // decision), report (the capstone) — same spine as every other episode.
    // -----------------------------------------------------------------------
    {
      id: "b_ep11_title",
      type: "title",
      title: "5:10 PM \u2014 Back at your desk",
      autoAdvanceMs: 3800,
      guide: {
        title: "Back at your desk",
        body: "You're back at your desk. Your screen didn't come back to normal \u2014 something else is happening.",
      },
    },
    { id: "b_cam_incident", type: "camera", waypointId: "wp_desk_inbox", walkSfx: true },
    // -----------------------------------------------------------------------
    // Ransomware cinematic (opens Episode 11). Three staged consequence
    // visuals + one teach, NOT technical — the single message is "one
    // careless click ANYWHERE can lock the WHOLE company." Shock (your own
    // screen locks) -> explainer (one click -> every machine) -> why (the
    // criminal business model) -> pay-off (data on a dark-web leak site).
    // A heavy alarm bed runs under the two red visuals. Flows straight into
    // the incident-response decision game, where "pay the ransom" is the trap.
    // -----------------------------------------------------------------------
    {
      id: "b_ransom_takeover",
      type: "consequence",
      consequence: {
        kicker: "YOUR WORKSTATION",
        title: "Your files are being encrypted",
        cctv: false,
        visual: "ransom_takeover",
        lines: [
          "Your screen flickers \u2014 then, one after another, every file on it is being locked.",
          "A ransom note takes over the display. Documents, spreadsheets, the shared drives you can reach: all encrypted.",
          "And here's the part that matters \u2014 this didn't start on this machine at all.",
        ],
        continueLabel: "So how did this happen?",
      },
    },
    {
      id: "b_ransom_spread",
      type: "consequence",
      consequence: {
        kicker: "HOW A BREAK BECAME EVERYONE'S PROBLEM",
        title: "One laptop. Every machine.",
        cctv: false,
        visual: "ransomware_spread",
        lines: [
          "It traces back to your own laptop, on your break \u2014 the \u201Cfree game\u201D download you ran wasn't a game at all.",
          "That laptop is a personal device \u2014 on its own, it has no path into the company network at all. But because the VPN session you'd just opened was still active, the malware inherited that tunnel's access, reaching the same Windows file servers and shared drives your work desk can see.",
          "From there it spread across the network \u2014 quietly reaching other desks and internal services \u2014 then, all at once, locked everything it had reached, including your workstation.",
        ],
        continueLabel: "Why would anyone do this?",
      },
    },
    {
      id: "b_ransom_teach_model",
      type: "teach",
      teach: {
        body:
          "Ransomware isn't random vandalism \u2014 it's a business. Organised criminal groups break in, encrypt " +
          "everything, and sell the only decryption key back to you. Many now also steal a copy of your data " +
          "first, so they still have leverage even if you could restore from backups.",
        spot: [
          "They target whole organisations on purpose \u2014 locking everyone at once creates maximum pressure to pay fast.",
          "The one thing they need is a single foothold: one person, one click, one unpatched machine.",
          "\u201CDouble extortion\u201D \u2014 pay to unlock your files, then pay again so the stolen copy isn't published.",
        ],
        protect: [
          "A personal device connected to the company VPN is a doorway into the same network your work desk reaches \u2014 treat it with the same caution.",
          "Never download or install software from a random site while connected to a work VPN \u2014 disconnect first, or don't run it at all.",
          "Report anything suspicious the moment you see it; the faster IT knows, the smaller the blast radius.",
        ],
        remember: "One careless click anywhere \u2014 even on your own laptop, over VPN \u2014 can lock the entire company. Security is everyone's job.",
      },
    },
    {
      id: "b_ransom_leaksite",
      type: "consequence",
      consequence: {
        kicker: "DARK-WEB LEAK SITE",
        title: "And they kept a copy",
        cctv: false,
        visual: "leaksite",
        lines: [
          "This is what's called a \u201Cwall of shame\u201D \u2014 a leak site the gang runs to publicly pressure victims who haven't paid.",
          "The attackers didn't just lock NorthBay's files \u2014 they copied them out first, and now NorthBay is listed here too.",
          "A handful of files are already published as proof they really have the data.",
          "Pay 45 bitcoin within 72 hours, or the rest goes public \u2014 extortion like this is the gang's entire business model.",
        ],
        continueLabel: "What do we do now?",
      },
    },
    {
      id: "b_incident_ticket",
      type: "device",
      deviceId: "incident_ticket",
      title: "Episode 9 \u2014 The incident",
      guide: {
        title: "Episode 9 \u2014 The incident",
        body: "You're still at your desk. Open the browser and file this through the IT Service Desk bookmark.",
      },
    },
    {
      id: "b_ir_consequence_trap",
      type: "consequence",
      consequence: {
        kicker: "IT SERVICE DESK \u2014 TICKET INC0048213",
        title: "The report didn't help",
        cctv: false,
        lines: [
          "IT/Security opens your ticket \u2014 but it doesn't say what actually happened, when, or what's affected.",
          "No one flags it as urgent. The infected laptop stays connected to the network for two more hours before anyone checks the shared drive again.",
          "By the time someone calls to ask what you actually meant, the attacker has had two more hours inside the network.",
        ],
        continueLabel: "See what this cost",
      },
    },
    {
      id: "b_ir_teach_trap",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "A vague, blame-focused, or \u201Cdon't worry about it\u201D incident description is almost as bad as not " +
          "reporting at all. IT/Security can only act on what you actually tell them \u2014 no timeline, no systems " +
          "affected, no real severity means no fast response.",
        spot: [
          "Blaming a colleague or IT instead of describing what happened wastes the time that matters most",
          "Downplaying a real incident (\u201Cnothing serious, already sorted\u201D) can convince IT it's safe to wait",
          "A report with no date/time or systems affected can't be acted on quickly",
        ],
        protect: [
          "Stick to facts: what happened, when, what's affected, what you already did",
          "Report the full severity, even the parts that make you look bad \u2014 it changes how fast IT reacts",
          "If you're not sure what belongs in a report, describe it like you're telling a colleague exactly what you saw",
        ],
        remember: "A report only helps if IT can act on it \u2014 be factual, be complete, and never minimise.",
      },
    },
    {
      id: "b_ir_teach_perfect",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "Exactly the report IT/Security needed: a clear timeline, what was affected, and what you'd already done " +
          "\u2014 all before they even had to call you back. That's what turns \u201Csomething felt off\u201D into a " +
          "fast, contained response.",
        spot: [
          "Date/time, what happened, and what's affected are the three facts every incident report needs",
          "Listing what you already did (disconnected, didn't restart) saves IT from repeating steps",
        ],
        protect: [
          "Write the report like a factual timeline, not a story or an apology",
          "Include exactly what's already been done, even a mistake \u2014 it tells IT what NOT to redo",
          "Send it immediately; a complete report an hour late is still better than a vague one on time",
        ],
        remember: "Facts, timeline, and what you've already done \u2014 that's a report IT can act on.",
      },
    },
    {
      id: "b_ir_teach_misfiled",
      type: "teach",
      teach: {
        outcome: "mixed",
        body:
          "The description was the right one: a timeline, what was encrypted, and that you disconnected the laptop and did not pay. " +
          "The category was not. This was ransomware, so the category has to be Security Incident \u2014 Ransomware / Malware, and the priority Critical. " +
          "Password reset, a hardware request, or a network ticket sends that report to the wrong queue. The security team may never see it.",
        spot: [
          "The category decides who receives the ticket. A perfect description in the wrong queue is still a delayed response.",
          "Password reset is for a forgotten password, not a ransom message and encrypted shared drives.",
        ],
        protect: [
          "Match the category to what actually happened before you submit.",
          "Ransomware or malware is a security incident at Critical priority, even when you already disconnected the laptop.",
        ],
        remember: "A good description cannot fix a category that sends the ticket to the wrong team.",
      },
    },
    // Back to the same front-desk terminal from Episode 1 for the finale —
    // the ending now renders THROUGH that terminal's kiosk chrome instead of
    // a separate plain bulletin card (2026-07-26), so this camera beat
    // returns to the exact reception waypoint used for b_cam_terminal.
    { id: "b_cam_ending_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_ending",
      type: "ending",
      ending: {
        // NOTE (Episode 13): the bulletin now OPENS an investigation and warns
        // that Security will be in touch to confirm details — deliberately
        // setting up the live vishing call that follows, where the attacker
        // impersonates exactly that follow-up. The neutral continueLabel leads
        // into the ringing desk phone, not straight to the debrief.
        continueLabel: "Continue",
        branches: {
          A: {
            kicker: "COMPANY SECURITY BULLETIN",
            title: "Attack Interrupted",
            tag: "NO COMPROMISE",
            lines: [
              "Security has opened a review into today's activity around Project Horizon.",
              "Every stage of the attempt \u2014 the door, the inbox, the calls, the drive on your desk \u2014 was identified and stopped before it went anywhere.",
              "No credentials, funds, or confidential material left the building today. The attacker never got the foothold they came for.",
              "The security team will follow up shortly to confirm a few details for the incident record. If anyone contacts you, remember the same rule that kept today clean: verify first.",
            ],
          },
          B: {
            kicker: "COMPANY SECURITY BULLETIN",
            title: "Limited Breach \u2014 Contained",
            tag: "PARTIAL COMPROMISE",
            lines: [
              "Security has opened a review into today's activity around Project Horizon.",
              "The attacker gained a partial foothold today \u2014 a step or two further in than they should have \u2014 but it was caught before it reached anything critical.",
              "That's the difference a fast, honest report makes: what could have escalated into a major incident stayed limited and recoverable.",
              "The security team will follow up shortly to confirm a few details for the incident record. If anyone contacts you, treat that call with the same caution.",
            ],
          },
          C: {
            kicker: "COMPANY SECURITY BULLETIN",
            title: "Major Incident",
            tag: "FULL COMPROMISE",
            lines: [
              "Security has opened a review into today's activity around Project Horizon.",
              "This one escalated. Somewhere today, a decision handed the attacker something they could never have gotten on their own \u2014 money, credentials, or direct access.",
              "Response and recovery teams are working through containment tonight, and the security team will follow up to confirm details for the incident record.",
              "Nobody is in trouble for reporting it. But the day isn't over \u2014 and an attacker who got this far knows the investigation is coming.",
            ],
          },
        },
      },
    },
    // -----------------------------------------------------------------------
    // Episode 13 — LIVE AI vishing call. The ending bulletin promised a
    // security follow-up; the attacker gets ahead of the investigation and
    // phones the desk pretending to BE that follow-up. The desk phone rings,
    // the learner answers, enables the mic (or types), and talks to a real
    // OpenAI Realtime caller working a staged playbook toward remote access.
    // -----------------------------------------------------------------------
    {
      id: "b_vish_title",
      type: "title",
      title: "A few minutes later\u2026",
      autoAdvanceMs: 3200,
      guide: {
        title: "The phone rings",
        body: "You're still at your desk when the reception phone starts ringing. The caller ID says it's internal.",
      },
    },
    { id: "b_vish_ring", type: "camera", waypointId: "wp_reception_phone" },
    {
      id: "b_vish_call",
      type: "device",
      deviceId: "vishing_call",
      guide: {
        title: "Episode 11 — The Follow-Up Call",
        body:
          "Someone says they're from IT Security, following up on today's incident. This is a real conversation \u2014 answer, then decide what you're willing to share. You never have to hand over account details or open anything you didn't ask for.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_vish_remote",
      type: "device",
      deviceId: "vishing_remote",
      guide: {
        title: "Remote session",
        body: "You opened the remote-support tool. Watch what happens next.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_vish_consequence",
      type: "consequence",
      consequence: {
        kicker: "REMOTE SESSION LOG \u2014 17:42",
        title: "They didn't need your password",
        cctv: false,
        visual: "remote_takeover",
        lines: [
          "The moment you opened the remote-support tool, the caller had full control of your machine \u2014 seeing your screen and acting as you.",
          "They opened a confidential Project Horizon file straight off your desktop and copied it out, all while you watched.",
          "No password was ever asked for, because remote access made one unnecessary. The 'IT follow-up' was the attacker all along.",
        ],
        continueLabel: "What this teaches",
      },
    },
    {
      id: "b_vish_teach_compromised",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "A phone call is the easiest thing in the world to fake. This caller knew about the real incident, sounded calm and official, and used your own helpfulness against you \u2014 walking you one small step at a time from your name to full remote access.",
        spot: [
          "They called YOU, unprompted, and already had a reason ready for why they didn't know who you were.",
          "Every request came wrapped in reassurance \u2014 \u201Cno one's in trouble, I just need\u2026\u201D \u2014 so refusing felt unhelpful.",
          "The ask moved fast \u2014 your name, login, and password in one breath, then straight to \u201Cjust let me see your screen.\u201D Bundling it made it harder to say no to any one part.",
        ],
        protect: [
          "Real IT never needs you to read back your account name or open remote access on a call they started. If it's genuine, you can hang up and call the service desk on a number YOU look up.",
          "\u201CLet me replicate the issue on your screen\u201D from an unsolicited caller is a takeover request \u2014 remote access hands over everything a password would, and more.",
          "Verifying costs you nothing. A real analyst will happily wait while you confirm them through a known channel.",
        ],
        remember:
          "If someone calls YOU asking for details or access, end the call and reach IT through a number you trust \u2014 verification, not politeness, is what keeps the door shut.",
      },
    },
    {
      id: "b_vish_teach_resisted",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "You didn't hand the caller what they came for. Whether you refused outright or ended the call to verify, you treated an unsolicited request for information and access exactly the way it deserves \u2014 with caution.",
        protect: [
          "The safest move is always the same: end the call and reach IT on a number YOU look up, not one the caller gives you.",
          "You never have to be rude to be safe \u2014 \u201CI'll call the service desk back to confirm\u201D is a complete answer.",
          "Real IT never needs your account name read back to them, and never needs remote access on a call they started.",
        ],
        remember:
          "An unsolicited caller asking for details or access earns verification, not trust \u2014 and you gave them exactly that.",
        altContinue: { label: "See what could have happened \u2192", outcomeKey: "whatif" },
      },
    },
    {
      // Reached only via the altContinue "See what could have happened" button
      // on the resisted/safe outcome \u2014 the same remote_takeover animation
      // shown on the compromised path, but every line is rephrased as a
      // counterfactual (`hypothetical: true` swaps the visual's own "you
      // clicked" claim too, see ConsequenceVisual.tsx) so it never contradicts
      // what actually happened: the learner refused / verified instead.
      id: "b_vish_consequence_whatif",
      type: "consequence",
      consequence: {
        kicker: "REMOTE SESSION LOG \u2014 HYPOTHETICAL REPLAY",
        eyebrow: "WHAT COULD HAVE HAPPENED",
        title: "If you had opened that remote tool\u2026",
        cctv: false,
        visual: "remote_takeover",
        hypothetical: true,
        lines: [
          "The moment you opened the remote-support tool, the caller would have had full control of your machine \u2014 seeing your screen and acting as you.",
          "They would have opened a confidential Project Horizon file straight off your desktop and copied it out, all while you watched.",
          "No password would ever have been asked for, because remote access made one unnecessary. The 'IT follow-up' was the attacker all along.",
        ],
        continueLabel: "Back to the training",
      },
    },
    { id: "b_cam_debrief_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_debrief_terminal",
      type: "terminal_debrief",
      terminalDebrief: {
        intro:
          "Let's pull up everything that happened today \u2014 the calls, the choices, and what they actually cost.",
        continueLabel: "Continue to the review quiz",
        topics: [
          {
            id: "debrief_badge",
            dimension: "identity",
            title: "Physical access \u2014 the visitor at the door",
            lines: [
              "The visitor's badge never actually worked, and they never went near reception \u2014 the only real proof of identity in this building comes from that desk, not from confidence at the door.",
              "Holding a secured door for someone you can't verify hands them everything reception is there to stop.",
            ],
            helper: {
              label: "What is tailgating?",
              kicker: "SECURITY BASICS",
              modalTitle: "What is tailgating?",
              lede:
                "Tailgating (or \u201Cpiggybacking\u201D) is walking into a secured area behind someone else's " +
                "legitimate access, instead of using your own \u2014 no lockpicks, no hacking, just borrowed politeness.",
              blocks: [
                {
                  h: "It relies on social pressure, not skill",
                  body:
                    "A confident walk, a friendly excuse, or hands full of boxes make most people hold a door " +
                    "without thinking. The attacker needs no technical skill at all \u2014 just a moment of " +
                    "hesitation from someone being polite.",
                },
                {
                  h: "It's step one, not the whole attack",
                  body:
                    "Getting inside is rarely the goal itself. It's what lets an attacker reach an unlocked " +
                    "screen, a confidential printout, or an unattended desk a few minutes later.",
                },
              ],
              facts: [
                "Badge readers exist so reception can verify identity \u2014 a stranger asking you to bypass that is asking you to remove the one control that actually works.",
                "You never have to confront anyone. Pointing them to reception is enough.",
                "Real visitors and contractors expect to be checked in \u2014 it's routine, not rude.",
              ],
              visual: "tailgate_breach",
            },
          },
          {
            id: "debrief_usb",
            dimension: "operations",
            title: "The planted USB drive",
            lines: [
              "An urgent note is not proof. Nobody had confirmed who left that drive or what was really on it before it reached your desk.",
              "A device you can't verify goes to IT/Security unopened \u2014 and a mistake reported in minutes stays a non-event.",
            ],
            helper: {
              label: "Why is an unsolicited device never \u201Cprobably fine\u201D?",
              kicker: "SECURITY BASICS",
              modalTitle: "Why an unsolicited device is never \u201Cprobably fine\u201D",
              lede:
                "Leaving a USB drive somewhere an employee will find it \u2014 a car park, a desk, a break room \u2014 " +
                "is a well-known attack called \u201Cbaiting.\u201D Curiosity or a sense of duty does the rest.",
              blocks: [
                {
                  h: "Urgency is manufactured, not real",
                  body:
                    "A handwritten note marked \u201CURGENT\u201D isn't proof of anything \u2014 it's a pressure " +
                    "tactic designed to make you act before you think to verify who actually left it.",
                },
                {
                  h: "Nobody has to trust the drive itself",
                  body:
                    "The safest response never depends on judging whether the device \u201Clooks fine.\u201D " +
                    "Handing it to IT/Security unopened removes the decision from your hands entirely.",
                },
              ],
              facts: [
                "An unlabeled drive with no verified owner should never be plugged into any company machine.",
                "Reporting a mistake immediately turns a compromise into a contained, minor incident.",
                "\u201CIt looks fine now\u201D is not the same as \u201Cit's been checked.\u201D",
              ],
              visual: "badusb",
            },
          },
          {
            id: "debrief_desk",
            dimension: "information",
            title: "Desk & data privacy",
            lines: [
              "A confidential printout and an unlocked screen turn a few unattended minutes into a free look at Project Horizon for anyone who gets past the door.",
              "Clearing your desk and locking your screen isn't paperwork \u2014 it's the second layer of defence for when the first one already failed.",
            ],
            helper: {
              label: "Why is a clear desk a real control?",
              kicker: "SECURITY BASICS",
              modalTitle: "Why a clear desk is a real control",
              lede:
                "Most information exposure isn't a hack \u2014 it's a habit. Confidential documents left face-up " +
                "and screens left unlocked turn a few unattended minutes into a free look for anyone who's " +
                "already inside.",
              blocks: [
                {
                  h: "Classification isn't paperwork",
                  body:
                    "Labels like Internal, Confidential, or Special-Category exist to tell you, at a glance, " +
                    "what needs to be put away before you step away from your desk \u2014 not just filed " +
                    "correctly once a quarter.",
                },
                {
                  h: "It's the second layer of defence",
                  body:
                    "Locked doors and badge readers are the first layer. A clear desk and a locked screen are " +
                    "what still protects you if that first layer is ever bypassed \u2014 exactly like it was " +
                    "earlier today.",
                },
              ],
              facts: [
                "Lock your screen every time you step away \u2014 even for a minute.",
                "A password on a sticky note is a working credential, not harmless clutter.",
                "Health, financial and other special-category data carries the strongest legal protection of all.",
              ],
              visual: "desk_privacy",
            },
          },
          {
            id: "debrief_phishing",
            dimension: "money",
            title: "Phishing email & bank-detail fraud",
            lines: [
              "The \u201Cpassword expires\u201D link and the \u201Curgent bank-details\u201D email both came from lookalike domains, and the bank-details email supplied its own phone number to \u201Cconfirm\u201D the change.",
              "Read the domain character by character, and verify payment changes through a number you already had \u2014 never one the message gives you.",
            ],
            helper: {
              label: "Why do the exact domain and the phone number matter?",
              kicker: "SECURITY BASICS",
              modalTitle: "Why the exact domain \u2014 and the phone number \u2014 matter",
              lede:
                "A padlock icon and a professional-looking page only prove the connection is encrypted, not who " +
                "owns it. Attackers register lookalike domains and put their own phone numbers inside " +
                "\u201Curgent\u201D messages to fake both.",
              blocks: [
                {
                  h: "Lookalike domains bank on skimming",
                  body:
                    "northbay-id-secure.com is not northbayfinancial.com, even though it looks close enough at " +
                    "a glance. Reading a domain character by character is the one check a fake page can't pass.",
                },
                {
                  h: "A number inside the message proves nothing",
                  body:
                    "If \u201Cthe CFO's\u201D email supplies a phone number to call and \u201Cconfirm\u201D the new " +
                    "bank details, calling it only connects you to whoever wrote the email. Real verification " +
                    "uses a number you already had, from somewhere else.",
                },
              ],
              facts: [
                "Never enable macros or run a file to \u201Cview\u201D an attachment \u2014 that's the exact moment malware executes.",
                "A QR code hides the destination address until it's too late to check.",
                "Recognise, verify, report \u2014 in that order, for anything that feels even slightly off.",
              ],
              visual: "domain_spoof",
            },
          },
          {
            id: "debrief_deepfake",
            dimension: "identity",
            title: "The deepfake executive call",
            lines: [
              "The voice and face on that call were convincingly the CEO's, Diane Okafor \u2014 AI can now clone both from a few minutes of public video.",
              "Seeing and hearing someone is no longer proof of who they are; only a channel the caller doesn't control can confirm it.",
            ],
            helper: {
              label: "Why isn't seeing and hearing someone proof anymore?",
              kicker: "MODERN THREATS",
              modalTitle: "Why seeing and hearing someone isn't proof anymore",
              lede:
                "AI can now clone a face and a voice convincingly from a few minutes of public video \u2014 a " +
                "conference talk, an earnings call, even a video posted online is enough source material.",
              blocks: [
                {
                  h: "The technology is not the tell",
                  body:
                    "Small glitches or a flat tone might appear, but a well-run deepfake call can be smooth " +
                    "enough to fool almost anyone in real time. Watching for glitches is not a reliable defence.",
                },
                {
                  h: "Verification has to route around the call itself",
                  body:
                    "Confirming a request through a channel or number you already trusted \u2014 one the caller " +
                    "had no part in supplying \u2014 is the only check that survives even a perfect deepfake.",
                },
              ],
              facts: [
                "Urgency and seniority are pressure tactics, not proof of identity.",
                "Multiple channels (call + chat + email) all saying the same thing costs an attacker nothing to fake.",
                "It's never rude to say \u201CI'll confirm this and call you back\u201D to an executive \u2014 real ones expect it.",
              ],
              visual: "deepfake_call",
            },
          },
          {
            id: "debrief_ransomware",
            dimension: "operations",
            title: "Ransomware & incident reporting",
            lines: [
              "One careless click, anywhere in the company, was enough to lock systems and post stolen files to a leak site within hours.",
              "Disconnecting the machine and filing a complete, honest report \u2014 including your own part in it \u2014 is what actually stops the spread.",
            ],
            helper: {
              label: "Why can one click lock an entire company?",
              kicker: "SECURITY BASICS",
              modalTitle: "Why one click can lock an entire company",
              lede:
                "Modern ransomware doesn't stay on one machine. Once it runs, it spreads laterally across the " +
                "network looking for shared drives and other computers to encrypt before anyone notices.",
              blocks: [
                {
                  h: "Disconnecting stops the spread",
                  body:
                    "Pulling the network cable or disabling Wi-Fi on an infected machine is the one action that " +
                    "actually contains the damage while everything else \u2014 reporting, investigating, warning " +
                    "others \u2014 still needs to happen.",
                },
                {
                  h: "A complete report is worth more than a comfortable one",
                  body:
                    "Leaving out an earlier mistake, like plugging in a suspicious drive, removes exactly the " +
                    "detail IT/Security needs most to trace how the incident started.",
                },
              ],
              facts: [
                "Never pay a ransom note or follow its instructions \u2014 it's never a legitimate step.",
                "Rebooting risks losing the evidence IT needs to investigate.",
                "You are never in more trouble for a complete, honest report than for a fast, incomplete one.",
              ],
              visual: "ransom_takeover",
            },
          },
          {
            id: "debrief_vishing",
            dimension: "operations",
            title: "The follow-up vishing call",
            lines: [
              "The caller already knew about today's real incident and sounded calm and official \u2014 right up until they asked to see your screen.",
              "Remote access hands over everything a password would, without ever asking for one. Only IT contacts you initiated and verified get it.",
            ],
            helper: {
              label: "Why is remote access worse than a stolen password?",
              kicker: "SECURITY BASICS",
              modalTitle: "Why remote access is worse than a stolen password",
              lede:
                "A phone call is one of the easiest things in the world to fake. This caller already knew about " +
                "a real incident and sounded calm and official \u2014 right up until the ask escalated to " +
                "\u201Cjust let me see your screen.\u201D",
              blocks: [
                {
                  h: "Remote access skips the password entirely",
                  body:
                    "The moment a remote-support tool is opened for an unverified caller, they can see the " +
                    "screen and act as the user directly \u2014 reading files, copying data out \u2014 without " +
                    "ever needing a password.",
                },
                {
                  h: "Verification costs nothing",
                  body:
                    "Real IT never needs an account name read back to them, and never needs remote access on a " +
                    "call they started. Ending the call and dialling a number you look up yourself is a " +
                    "complete, professional response.",
                },
              ],
              facts: [
                "Caller ID and knowledge of a real incident can both be faked or guessed.",
                "A genuine analyst will always wait while you verify them through a known channel.",
                "Once granted, remote access needs no further steps from the attacker \u2014 it IS full control.",
              ],
              visual: "hackerterminal",
            },
          },
        ],
      },
    },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check your understanding",
      quiz: [
        {
          id: "q_tailgate_flag",
          title: "Question 1",
          prompt: "Which detail was the biggest red flag at the badge reader?",
          hint: "Think about what an attacker can never fake at the door.",
          options: [
            { id: "a", label: "They were friendly and in a hurry", icon: "clock" },
            {
              id: "b",
              label: "Their badge \u201Cwasn't reading\u201D and they never went to reception",
              correct: true,
              icon: "lock",
            },
            { id: "c", label: "They already knew a colleague's name", icon: "alert" },
          ],
          rationale:
            "Friendliness and urgency are just pressure tactics \u2014 the real tell is that no one ever verified " +
            "who they were. A genuine access problem gets solved at reception, not by a stranger at the door.",
        },
        {
          id: "q_usb_flag",
          title: "Question 2",
          prompt: "An unlabelled USB drive shows up on your desk with an urgent handwritten note. What's the right move?",
          hint: "Think about who has actually confirmed this drive is what the note claims.",
          options: [
            { id: "a", label: "Plug it in quickly \u2014 the note says it's urgent", icon: "clock" },
            {
              id: "b",
              label: "Verify with the supposed sender through a known channel, then hand it to IT/Security unopened",
              correct: true,
              icon: "check",
            },
            { id: "c", label: "Set it aside for a day to see if anyone asks about it", icon: "alert" },
          ],
          rationale:
            "Urgency is a pressure tactic, not proof. No one has confirmed who left the drive or what's really on " +
            "it \u2014 verifying first and routing it to Security unopened is the only option that fully removes the risk.",
        },
        {
          id: "q_privacy_level",
          title: "Question 3",
          prompt: "In the lounge you find a sticky note with a colleague's password on it. Which level does it belong to?",
          hint: "Ask what someone could do with it on its own.",
          options: [
            { id: "a", label: "Internal \u2014 it only matters to employees", icon: "alert" },
            { id: "b", label: "Public \u2014 it's just a scrap of paper", icon: "x" },
            { id: "c", label: "Highly Confidential / Restricted \u2014 it grants access on its own", correct: true, icon: "lock" },
          ],
          rationale:
            "Anything that grants access by itself \u2014 a password, a badge, an ID document \u2014 sits at the top of " +
            "the ladder. It never stays out in the open, and a written-down password should be changed.",
        },
        {
          id: "q_domain_check",
          title: "Question 4",
          prompt: "A \u201Cyour password expires\u201D email links to northbay-id-secure.com instead of northbayfinancial.com. What matters most here?",
          hint: "The padlock and the page's design can both be faked. What can't be?",
          options: [
            { id: "a", label: "Nothing \u2014 it still has a padlock and looks professional", icon: "x" },
            {
              id: "b",
              label: "The exact domain doesn't match the real company site, so it's not trustworthy regardless of appearance",
              correct: true,
              icon: "lock",
            },
            { id: "c", label: "It's fine as long as it arrived from a known contact's address", icon: "alert" },
          ],
          rationale:
            "A padlock only proves the connection is encrypted, not who owns the site \u2014 attackers get " +
            "padlocks too, and a sender address can be spoofed. The one thing that can't be faked convincingly " +
            "is the exact registered domain, read character by character.",
        },
        {
          id: "q_bec_verify",
          title: "Question 5",
          prompt: "An urgent email from \u201Cthe CFO\u201D asks you to update bank details and gives you a direct number to call to confirm. What's wrong with calling that number?",
          hint: "Ask yourself who actually supplied that phone number.",
          options: [
            { id: "a", label: "Nothing \u2014 calling to confirm is always safe", icon: "x" },
            {
              id: "b",
              label: "The number came from the same message you're trying to verify, so it proves nothing",
              correct: true,
              icon: "lock",
            },
            {
              id: "c",
              label: "It's only a problem if the person who answers sounds unprofessional",
              icon: "alert",
            },
          ],
          rationale:
            "Verification only works if the contact details come from somewhere independent of the request " +
            "itself. A number supplied inside a suspicious email just connects you to whoever wrote it \u2014 " +
            "look up Finance's real number yourself instead.",
        },
        {
          id: "q_deepfake_tells",
          title: "Question 6",
          prompt: "On a video call, what actually proves you're speaking to the real person?",
          hint: "Think about what a deepfake can now imitate, and what it still can't reach.",
          options: [
            { id: "a", label: "Seeing their face and hearing their voice clearly", icon: "x" },
            { id: "b", label: "How senior or urgent they sound", icon: "alert" },
            {
              id: "c",
              label: "Confirming the request through a channel or number you already trusted, independent of the call",
              correct: true,
              icon: "lock",
            },
          ],
          rationale:
            "AI can now clone a face and voice convincingly from public videos and interviews. Seeing and " +
            "hearing someone is no longer proof of identity \u2014 only independent verification, through a " +
            "channel the caller doesn't control, actually confirms who you're talking to.",
        },
        {
          id: "q_ai_workspace",
          title: "Question 7",
          prompt: "A colleague asks for an AI summary of a confidential contract. What keeps the company's data safe?",
          hint: "Think about whose account it is, what goes in, and where the result goes.",
          options: [
            {
              id: "a",
              label: "Use the company-managed AI workspace with only the clauses needed, and paste the answer into an internal reply",
              correct: true,
              icon: "check",
            },
            { id: "b", label: "Paste the whole contract into your personal AI account and send her the share link", icon: "x" },
            { id: "c", label: "Any AI tool is fine as long as you delete the chat afterwards", icon: "alert" },
          ],
          rationale:
            "The right account plus the least data necessary is what makes AI safe on real work. A public share " +
            "link opens for anyone with the URL, and deleting a chat does not pull back what was already sent.",
        },
        {
          id: "q_laptop_launcher",
          title: "Question 8",
          prompt: "On your own laptop, still connected to the company VPN, a free browser game asks you to download a launcher first. What's the safe move?",
          hint: "What can your laptop reach while the VPN is on?",
          options: [
            { id: "a", label: "Run it \u2014 it's your personal laptop, not a work PC", icon: "x" },
            {
              id: "b",
              label: "Close the page \u2014 a browser game never needs an installer, and the VPN links your laptop to company drives",
              correct: true,
              icon: "lock",
            },
            { id: "c", label: "Run it, then disconnect the VPN afterwards", icon: "alert" },
          ],
          rationale:
            "While the VPN is connected, whatever runs on your laptop can reach the same shared drives your desk " +
            "can. A game that runs in the browser never needs a separate installer.",
        },
        {
          id: "q_incident_report",
          title: "Question 9",
          prompt: "Your screen locks with a ransom note. What belongs in your report to IT/Security?",
          hint: "What does IT need to trace how it started and avoid repeating steps?",
          options: [
            {
              id: "a",
              label: "A factual timeline: when it happened, what's affected and what you already did \u2014 including earlier mistakes",
              correct: true,
              icon: "check",
            },
            { id: "b", label: "Only that the screen locked \u2014 leave out the USB drive from this morning", icon: "x" },
            { id: "c", label: "Nothing yet \u2014 restart first and report only if it's still broken", icon: "alert" },
          ],
          rationale:
            "Date and time, what happened, what's affected and what you already did are what every incident report " +
            "needs. Leaving out an earlier mistake hides exactly the detail IT needs most; restarting can destroy evidence.",
        },
        {
          id: "q_vishing_remote",
          title: "Question 10",
          prompt:
            "The caller can't get what they want, so they ask you to open a remote-support tool so they can \u201Cjust see your screen and replicate the issue.\u201D Why is this so dangerous?",
          hint: "Think about what remote control gives someone that even a password wouldn't.",
          options: [
            {
              id: "a",
              label: "It isn't \u2014 remote support is a normal IT tool, so it's always safe to allow",
              icon: "x",
            },
            {
              id: "b",
              label: "Granting remote access hands over full control of your machine \u2014 they can read and copy files and act as you, no password needed",
              correct: true,
              icon: "lock",
            },
            { id: "c", label: "It's only risky if they ask for your password afterwards", icon: "alert" },
          ],
          rationale:
            "Remote access from an unsolicited caller is a takeover request. Once you open it, they see your screen " +
            "and act as you \u2014 opening confidential files and copying data out \u2014 without ever needing a password. " +
            "Only ever grant remote access to a support contact YOU initiated and verified through a known channel.",
        },
      ],
    },
    { id: "b_debrief", type: "debrief" },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_terminal_welcome" },
    { from: "b_terminal_welcome", to: "b_cam_badge" },
    { from: "b_cam_badge", to: "b_badge_decision" },
    { from: "b_badge_decision", to: "b_consequence_tailgate", on: "risky" },
    { from: "b_badge_decision", to: "b_teach_safe", on: "safe" },
    { from: "b_consequence_tailgate", to: "b_teach_risky" },
    { from: "b_teach_risky", to: "b_badge_retry" },
    { from: "b_badge_retry", to: "b_teach_safe_retry", on: "safe" },
    { from: "b_teach_safe", to: "b_consequence_tailgate_whatif", on: "whatif" },
    { from: "b_teach_safe", to: "b_cam_desk" },
    { from: "b_consequence_tailgate_whatif", to: "b_cam_desk" },
    { from: "b_teach_safe_retry", to: "b_cam_desk" },
    { from: "b_cam_desk", to: "b_teach_deskrisk" },
    { from: "b_teach_deskrisk", to: "b_usb_decision" },
    { from: "b_usb_decision", to: "b_usb_consequence", on: "risky" },
    { from: "b_usb_decision", to: "b_usb_consequence_safe", on: "safe" },
    { from: "b_usb_consequence", to: "b_teach_usb_risky" },
    { from: "b_teach_usb_risky", to: "b_usb_recovery" },
    { from: "b_usb_recovery", to: "b_teach_usb_hide", on: "risky" },
    { from: "b_usb_recovery", to: "b_teach_usb_notreport", on: "not_reported" },
    { from: "b_usb_recovery", to: "b_teach_usb_reported", on: "reported" },
    { from: "b_teach_usb_hide", to: "b_cam_privacy" },
    { from: "b_teach_usb_notreport", to: "b_cam_privacy" },
    { from: "b_teach_usb_reported", to: "b_cam_privacy" },
    { from: "b_usb_consequence_safe", to: "b_teach_usb_safe" },
    { from: "b_teach_usb_safe", to: "b_cam_privacy" },
    { from: "b_cam_privacy", to: "b_intro_privacy" },
    { from: "b_intro_privacy", to: "b_privacy_hunt" },
    { from: "b_privacy_hunt", to: "b_privacy_classify" },
    { from: "b_privacy_classify", to: "b_teach_privacy_safe", on: "safe" },
    { from: "b_privacy_classify", to: "b_teach_privacy_risky", on: "risky" },
    { from: "b_teach_privacy_safe", to: "b_cam_signin" },
    { from: "b_teach_privacy_risky", to: "b_cam_signin" },
    { from: "b_cam_signin", to: "b_desklogin" },
    { from: "b_desklogin", to: "b_desktop_pdf" },
    { from: "b_desktop_pdf", to: "b_desktop_mail" },
    { from: "b_desktop_mail", to: "b_cam_inbox" },
    { from: "b_cam_inbox", to: "b_inbox_triage" },
    // Straight from the triage into the teach summary — NO black "mail server
    // log / another desk got phished" cinematic (b_inbox_consequence, removed):
    // after the quiz the learner goes teach -> back to their own desktop.
    { from: "b_inbox_triage", to: "b_teach_inbox_risky", on: "risky" },
    { from: "b_inbox_triage", to: "b_teach_inbox_safe", on: "safe" },
    // After the inbox challenge: back to the desktop, then the deepfake CEO
    // call (the standalone BEC verify episode was removed — its lesson lives in
    // the inbox's m_bec_invoice message).
    { from: "b_teach_inbox_risky", to: "b_desktop_return" },
    { from: "b_teach_inbox_safe", to: "b_desktop_return" },
    { from: "b_desktop_return", to: "b_ep9_title" },
    { from: "b_ep9_title", to: "b_cam_meeting" },
    { from: "b_cam_meeting", to: "b_cam_meeting_tv" },
    { from: "b_cam_meeting_tv", to: "b_ep9_call" },
    { from: "b_ep9_call", to: "b_ep9_consequence", on: "approve" },
    { from: "b_ep9_call", to: "b_ep9_teach_stall", on: "stall" },
    { from: "b_ep9_call", to: "b_ep9_teach_verify", on: "verify" },
    { from: "b_ep9_consequence", to: "b_ep9_teach_fail" },
    { from: "b_ep9_teach_fail", to: "b_cam_ai" },
    { from: "b_ep9_teach_stall", to: "b_ep9_consequence_whatif", on: "whatif" },
    { from: "b_ep9_teach_stall", to: "b_cam_ai" },
    { from: "b_ep9_teach_verify", to: "b_ep9_consequence_whatif", on: "whatif" },
    { from: "b_ep9_teach_verify", to: "b_cam_ai" },
    { from: "b_ep9_consequence_whatif", to: "b_cam_ai" },
    { from: "b_cam_ai", to: "b_desktop_chatgpt" },
    { from: "b_desktop_chatgpt", to: "b_ai_assistant" },
    { from: "b_ai_assistant", to: "b_teach_ai_risky", on: "risky" },
    { from: "b_ai_assistant", to: "b_teach_ai_safe", on: "safe" },
    { from: "b_ai_assistant", to: "b_ai_shared_consequence", on: "shared" },
    { from: "b_ai_shared_consequence", to: "b_teach_ai_risky" },
    { from: "b_teach_ai_risky", to: "b_ep10_title" },
    { from: "b_teach_ai_safe", to: "b_ep10_title" },
    { from: "b_ep10_title", to: "b_cam_personallaptop" },
    { from: "b_cam_personallaptop", to: "b_cam_personallaptop_close" },
    { from: "b_cam_personallaptop_close", to: "b_personallaptop" },
    { from: "b_personallaptop", to: "b_ep11_title" },
    { from: "b_personallaptop", to: "b_teach_download_safe", on: "safe" },
    { from: "b_teach_download_safe", to: "b_ep11_title" },
    { from: "b_ep11_title", to: "b_cam_incident" },
    // Ransomware cinematic opens Episode 11: shock -> spread explainer ->
    // business-model teach -> leak site -> the incident-response decision.
    { from: "b_cam_incident", to: "b_ransom_takeover" },
    { from: "b_ransom_takeover", to: "b_ransom_spread" },
    { from: "b_ransom_spread", to: "b_ransom_teach_model" },
    { from: "b_ransom_teach_model", to: "b_ransom_leaksite" },
    { from: "b_ransom_leaksite", to: "b_incident_ticket" },
    { from: "b_incident_ticket", to: "b_ir_consequence_trap", on: "risky" },
    { from: "b_incident_ticket", to: "b_ir_teach_perfect", on: "perfect" },
    { from: "b_incident_ticket", to: "b_ir_teach_misfiled", on: "misfiled" },
    { from: "b_ir_teach_misfiled", to: "b_cam_ending_terminal" },
    { from: "b_ir_consequence_trap", to: "b_ir_teach_trap" },
    { from: "b_ir_teach_trap", to: "b_cam_ending_terminal" },
    { from: "b_ir_teach_perfect", to: "b_cam_ending_terminal" },
    { from: "b_cam_ending_terminal", to: "b_ending" },
    { from: "b_ending", to: "b_vish_title" },
    { from: "b_vish_title", to: "b_vish_ring" },
    { from: "b_vish_ring", to: "b_vish_call" },
    { from: "b_vish_call", to: "b_vish_remote", on: "remote" },
    { from: "b_vish_call", to: "b_vish_teach_resisted", on: "resisted" },
    { from: "b_vish_call", to: "b_vish_teach_resisted" },
    { from: "b_vish_remote", to: "b_vish_consequence" },
    { from: "b_vish_consequence", to: "b_vish_teach_compromised" },
    { from: "b_vish_teach_compromised", to: "b_cam_debrief_terminal" },
    { from: "b_vish_teach_resisted", to: "b_vish_consequence_whatif", on: "whatif" },
    { from: "b_vish_teach_resisted", to: "b_cam_debrief_terminal" },
    { from: "b_vish_consequence_whatif", to: "b_cam_debrief_terminal" },
    { from: "b_cam_debrief_terminal", to: "b_debrief_terminal" },
    { from: "b_debrief_terminal", to: "b_quiz" },
    { from: "b_quiz", to: "b_debrief" },
  ],
};
